// pages/Community/CommunityConnectors.jsx
//
// The "Connectors" tab of the Community hub. Browser-automation of social
// platforms (LinkedIn + Facebook) for the operations the official platform APIs
// do NOT expose: inviting people and sending in-app messages.
//
// A server-side headless browser drives a real login. The "Streamed Login"
// modal renders the browser's live screenshot and forwards click/keyboard input
// to it, so the operator logs in normally (including 2FA) — we capture the
// session afterwards and never store their password. Every invite/message is
// tracked as an activity, and every targeted person rolls up into a contact
// directory, shown across four sub-tabs: Accounts | Invites | Messages | Contacts.
//
// Matches the sibling Community pages: brand from redux (s.user.brands) + a brand
// <select>, addNotification toasts, and the shared fetch helpers (Bearer auth).

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import addNotification from "helpers/notify";
import SkoolPanel from "./SkoolPanel";
import SkoolConversations from "./SkoolConversations";
import SkoolProspects from "./SkoolProspects";
import SkoolMentions from "./SkoolMentions";
import SkoolWelcomes from "./SkoolWelcomes";
import SkoolOnboarding from "./SkoolOnboarding";
import SkoolNudges from "./SkoolNudges";
import {
  url,
  runOp,
  getActivities,
  retryActivity,
  retryAllActivities,
  harvestContacts,
  discoverContacts,
  categorizeContacts,
  generateOutreach,
  draftMessage,
  getScoredContacts,
  getAgentToken,
  getAgentStatus,
  getConnectorPrefs,
  saveConnectorPrefs,
  getSessions,
  getConnectorStats,
  getConnectorEvents,
  bulkCancelActivities,
  toggleSessionPause,
  getContactTimeline,
  getAutoInviteConfig,
  getAutoInviteAll,
  saveAutoInviteConfig,
  getAutoInviteStatus,
  getConversations,
  getConversation,
  generateAiReply,
  updateConversation,
  setBrandGoal,
  getStatsDetail,
  getSkoolAccounts,
  getSkoolProspects,
  setSkoolProspectState,
} from "api/connectors/connectors";
import { COLUMNS as PROSPECT_STATES } from "./SkoolProspects";

// Skool prospect state for the person a Skool comment/DM activity was aimed at
// (worker services/prospectClassifier). The person is the post author for a
// group comment (payload.authorHandle), the member for a welcome comment
// (target.url /@handle), and whoever mentioned us for a mention reply
// (payload.userId — recorded from 2026-09-26; older mention replies carry no
// identity and show no state). Keyed per brand: the same handle in another
// brand's community is a different relationship.
const PROSPECT = Object.fromEntries(PROSPECT_STATES.map((c) => [c.key, c]));
const PROSPECT_SOURCE = { manual: "set by you", booking: "booking", rule: "rule", ai: "AI" };
export function skoolPersonKeys(a) {
  if (!a || a.platform !== "skool") return [];
  const p = a.payload || {};
  const b = String(a.brand_id || "");
  const fromUrl = String(((a.target && a.target.url) || "").split("/@")[1] || "").toLowerCase();
  const handle = String(p.authorHandle || p.handle || fromUrl || "").toLowerCase();
  return [handle && `${b}|h:${handle}`, p.userId && `${b}|u:${p.userId}`].filter(Boolean);
}

// Built on demand from the extension directory that ships with the app, so the
// download is ALWAYS the promoted version. This used to point at a hand-copied
// file under uploads/ — which is gitignored, so no promote could refresh it and
// the site served v0.44.0 for days while the repo was on v0.46.0.
//
// The `t` param is a cache-buster, and it is not paranoia: Cloudflare caches by
// file extension, saw a .zip, and pinned it for four hours — so the edge served
// 0.46.0 while the origin was already on 0.47.0. The server now sends no-store,
// but that only stops FUTURE caching; a unique key per page load means an
// operator can never be handed a stale build while an old entry ages out.
const EXTENSION_ZIP_URL = `${url}/api/connectors/extension.zip?t=${Date.now()}`;

// Canonical platform catalog — mirrors the brand-settings Social Accounts page.
// The server's /prefs endpoint decides which of these a company may operate on
// (super-admin allowlist); the page only ever shows the allowed subset.
const PLATFORM_META = {
  linkedin:  { label: "LinkedIn",  icon: "fab fa-linkedin",  color: "#0a66c2" },
  facebook:  { label: "Facebook",  icon: "fab fa-facebook",  color: "#1877f2" },
  twitter:   { label: "X / Twitter", icon: "fab fa-twitter", color: "#1d9bf0" },
  instagram: { label: "Instagram", icon: "fab fa-instagram", color: "#e1306c" },
  pinterest: { label: "Pinterest", icon: "fab fa-pinterest", color: "#e60023" },
  youtube:   { label: "YouTube",   icon: "fab fa-youtube",   color: "#ff0000" },
  tiktok:    { label: "TikTok",    icon: "fab fa-tiktok",    color: "#010101" },
  snapchat:  { label: "Snapchat",  icon: "fab fa-snapchat-ghost", color: "#fffc00" },
  google:    { label: "Google",    icon: "fab fa-google",    color: "#4285f4" },
  // Skool is not browser-automated like the others — it's a direct JSON-API
  // channel driven from the operator's harvested session, so it has no login
  // stream, but its activities land in the same feed.
  skool:     { label: "Skool",     icon: "fas fa-graduation-cap", color: "#f6c344" },
};
const PLATFORM_LABEL = Object.fromEntries(Object.entries(PLATFORM_META).map(([k, v]) => [k, v.label]));
const OP_LABEL = {
  message: "Message",
  invite: "Invite",
  follow_page: "Follow page",
  invite_to_page: "Page invite",
  search_invite: "Search & invite",
  comment: "Comment",
  publish: "Post",
  dm: "DM",
  upload: "Media upload",
  raw: "API call",
};
const OP_CATEGORY = {
  message: "profile",      // profile actions
  invite: "profile",       // profile actions
  follow_page: "page",     // page actions
  invite_to_page: "page",  // page actions
  search_invite: "search", // search
};

const INVITE_SUB_TABS = [
  { key: "all", label: "All invites" },
  { key: "invite", label: "Profile invites" },
  { key: "invite_to_page", label: "Page invites" },
  { key: "search_invite", label: "Search & invite" },
];
const MESSAGE_SUB_TABS = [
  { key: "all", label: "All" },
  { key: "message", label: "Messages" },
  { key: "comment", label: "Comments" },
  { key: "follow_page", label: "Follow page" },
];

const SUB_TABS = [
  { key: "accounts", label: "Accounts" },
  { key: "invites", label: "Invites" },
  { key: "messages", label: "Messages" },
  { key: "contacts", label: "Contacts" },
  // Skool DM threads — outreach conversations, tracked to conversion.
  { key: "skool", label: "Skool DMs" },
  // Where each Skool person stands (classifier) + the operator override.
  { key: "skoolProspects", label: "Skool prospects" },
  // Mentions the mention lane answered: who, what they said, our reply, their state.
  { key: "skoolMentions", label: "Skool mentions" },
  // Welcome comments on new members' posts, and where each member stands now.
  { key: "skoolWelcomes", label: "Skool welcomes" },
  // The onboarding ladder per new member, and whether the classifier stopped it.
  { key: "skoolOnboarding", label: "Skool onboarding" },
  // Invitations to quiet existing members (max 2), and whether another may come.
  { key: "skoolNudges", label: "Skool nudges" },
];

const fmt = (d) => { try { return d ? new Date(d).toLocaleString() : "—"; } catch (_) { return "—"; } };
const truncate = (s, n = 80) => { const t = String(s || ""); return t.length > n ? t.slice(0, n) + "…" : t; };

// Coloured status pill. Maps both session statuses (active/needs_login/expired)
// and activity statuses (success/queued/failed/…) onto a consistent palette.
function StatusBadge({ status }) {
  const s = String(status || "").toLowerCase();
  let bg = "#eceef1", fg = "#667085", label = status || "—";
  if (s === "active" || s === "success" || s === "completed" || s === "connected") { bg = "#e7f5ee"; fg = "#1a7f37"; }
  else if (s === "needs_login" || s === "queued" || s === "pending" || s === "running") { bg = "#fff4e5"; fg = "#9a6700"; }
  else if (s === "failed" || s === "error" || s === "expired") { bg = "#fdecea"; fg = "#c0392b"; }
  return (
    <span style={{ display: "inline-block", fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: bg, color: fg }}>
      {String(label).replace(/_/g, " ")}
    </span>
  );
}

// Relevance tier for a contact vs the selected brand.
function TierBadge({ tier, score }) {
  if (!tier) return <span className="text-muted" style={{ fontSize: "0.74rem" }}>unscored</span>;
  const map = { high: ["#e7f5ee", "#1a7f37"], medium: ["#fff4e5", "#9a6700"], low: ["#f0f1f4", "#667085"] };
  const [bg, fg] = map[tier] || ["#eceef1", "#667085"];
  return <span style={{ display: "inline-block", fontSize: "0.72rem", fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: bg, color: fg }}>{tier}{score != null ? ` ${score}` : ""}</span>;
}

const card = { border: "1px solid #e3e6ec", borderRadius: 10, padding: 14, background: "#fff" };

// Small stat block for the permanent stats bar.
// Clickable when onClick is provided — opens a drill-down modal.
function StatBlock({ label, value, color, bg, onClick, title }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap", cursor: onClick ? "pointer" : "default" }}
      title={title || (onClick ? "Click to see details" : "")}
      onClick={onClick}>
      <span style={{ color: "#8a8f98" }}>{label}</span>
      <span style={{ display: "inline-block", fontWeight: 700, padding: "1px 7px", borderRadius: 10, background: bg, color, fontSize: "0.74rem" }}>{value}</span>
    </span>
  );
}

const CommunityConnectors = () => {
  const brandsRedux = useSelector((s) => s.user.brands) || [];

  // ---- Unified session controls (persisted to /prefs) --------------------
  // The page is a company-wide console: checked brands take part in matching
  // AND execution; checked platforms are where actions run. Learning/recording
  // always runs regardless. Empty checkedBrands = ALL brands.
  const [platformCatalog, setPlatformCatalog] = useState([]);   // super-admin-allowed platform keys
  const [checkedPlatforms, setCheckedPlatforms] = useState([]); // session platform checkboxes
  const [checkedBrands, setCheckedBrands] = useState([]);       // session brand checkboxes ([] = all)
  const [skoolBrand, setSkoolBrand] = useState("");             // which brand's Skool binding is shown
  const [skoolBoundBrands, setSkoolBoundBrands] = useState([]); // brands that actually have a Skool community bound
  const [prefsReady, setPrefsReady] = useState(false);

  // Which brand's Skool data to show. Defaulting to brandsRedux[0] — "whatever
  // brand happens to be first" — silently pointed the DM threads tab at a brand
  // with no Skool binding at all, so 36 real conversations rendered as "No Skool
  // conversations yet". Only one brand usually has a community bound, so resolve
  // to THAT and fall back to the first brand only if nothing is bound.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSkoolAccounts();
        const bound = ((res && res.accounts) || [])
          .filter((a) => a && a.brand_id && a.slug)
          .map((a) => String(a.brand_id));
        if (cancelled) return;
        setSkoolBoundBrands(bound);
        setSkoolBrand((cur) => cur || bound[0] || "");
      } catch (_) { /* the picker still works, it just has no smart default */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // The brand every Skool view reads. Never silently "the first brand".
  const skoolBrandId = skoolBrand || skoolBoundBrands[0] || (brandsRedux[0] && brandsRedux[0]._id) || "";

  // Brands actually operated on this session (empty selection = all brands).
  const allBrandIds = brandsRedux.map((b) => String(b._id));
  const activeBrandIds = checkedBrands.length ? checkedBrands : allBrandIds;
  const hasContext = activeBrandIds.length > 0;

  const [tab, setTab] = useState("accounts");
  const [inviteSubTab, setInviteSubTab] = useState("all");
  const [messageSubTab, setMessageSubTab] = useState("all");
  const [skoolProspects, setSkoolProspects] = useState({});   // "<brand>|h:<handle>" / "<brand>|u:<userId>" → ProspectState
  const loadSkoolProspects = useCallback(async () => {
    try {
      const res = await getSkoolProspects({});
      const map = {};
      ((res && res.prospects) || []).forEach((p) => {
        const b = String(p.brand_id || "");
        if (p.handle) map[`${b}|h:${String(p.handle).toLowerCase()}`] = p;
        if (p.userId) map[`${b}|u:${p.userId}`] = p;
      });
      setSkoolProspects(map);
    } catch (_) { /* rows still render without states */ }
  }, []);
  const prospectForActivity = (a) => {
    for (const k of skoolPersonKeys(a)) if (skoolProspects[k]) return skoolProspects[k];
    return null;
  };
  const changeProspectState = async (p, state) => {
    if (!p || !state) return;
    try {
      await setSkoolProspectState(p._id, state, state === "auto" ? "" : "set from Connectors → Comments");
      addNotification(state === "auto" ? "Handed back to the classifier" : `Now: ${(PROSPECT[state] || {}).label || state}`, "success");
      loadSkoolProspects();
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not change the state", "error"); }
  };

  // Data per sub-tab.
  const [invites, setInvites] = useState([]);
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);

  // In-browser agent (Prigmar Connector extension): token + liveness.
  const [agentToken, setAgentToken] = useState("");
  const [agentOnline, setAgentOnline] = useState(false);
  const [agentSeen, setAgentSeen] = useState(null);
  const agentPollRef = useRef(null);

  // Sessions for pause/resume
  const [sessions, setSessions] = useState([]);

  // Timeline modal: shows all activities for a contact
  const [timelineContact, setTimelineContact] = useState(null); // { name, url, activities, loading }

  // The brand a manual op runs AS. Unambiguous when exactly one brand is in
  // play; otherwise the operator picks, because there is no sensible default
  // for "which identity does this DM come from".
  const [opBrand, setOpBrand] = useState("");
  // One brand in play is unambiguous; more than one has to be chosen.
  const opBrandId = activeBrandIds.length === 1 ? activeBrandIds[0] : (activeBrandIds.includes(opBrand) ? opBrand : "");
  // The brand to act as for a CONTACT. Normally the contact carries its own,
  // which is always right. When it does not, fall back only where there is
  // nothing to get wrong — a single active brand — and otherwise return "" so
  // the caller refuses. Taking activeBrandIds[0] here would send a real DM
  // from an arbitrary brand, the same failure the op form had.
  const brandForContact = (contact) => String((contact && contact.brandId) || "")
    || (activeBrandIds.length === 1 ? activeBrandIds[0] : "");

  // "Run an operation" form.
  const [op, setOp] = useState({ op: "message", targetUrl: "", targetName: "", text: "", note: "", keyword: "", maxInvites: "", pageUrl: "" });
  const [opBusy, setOpBusy] = useState(false);

  // Audience tools (harvest → categorize → targeted outreach). Platforms come
  // from the global platform checkboxes now, not a per-form picker.
  const [tierFilter, setTierFilter] = useState("all");
  const [contactBusy, setContactBusy] = useState("");
  const [discoverKeyword, setDiscoverKeyword] = useState(""); // IG discovery search term (name/industry)

  // Quick invite/message modal — open from the Contacts table per row.
  const [quickModal, setQuickModal] = useState(null); // { contact, op, text, note, busy }

  // Conversation sidebar — open from Invites/Messages tables.
  const [convSidebar, setConvSidebar] = useState(null); // { conversation, thread, loading }

  // Permanent stats bar + activity log — polled separately from per-tab data.
  const [stats, setStats] = useState(null); // { activities, harvest, outreach, extension, successRate, lastEvent }
  const [logEvents, setLogEvents] = useState([]);
  const [logExpanded, setLogExpanded] = useState(false);
  const [logFilter, setLogFilter] = useState("all"); // event type filter
  const [logHasMore, setLogHasMore] = useState(false);
  const [logCursor, setLogCursor] = useState(null);
  const [logLoading, setLogLoading] = useState(false);
  const logPollRef = useRef(null);
  const statsPollRef = useRef(null);

  // Auto-invite config + status.
  const [autoInvite, setAutoInvite] = useState(null); // { config, platformLimits }
  const [autoInviteStatus, setAutoInviteStatus] = useState(null); // { enabled, platforms:{linkedin:{caps,used,remaining},...} }
  const [autoInviteSaving, setAutoInviteSaving] = useState(false);
  const [autoInvitePlatform, setAutoInvitePlatform] = useState("linkedin"); // which platform tab is selected
  const [autoInviteBrand, setAutoInviteBrand] = useState(""); // which brand's config is expanded for editing ("" = none)
  const [autoInviteMaster, setAutoInviteMaster] = useState(false); // company-wide master switch
  const [autoInviteConfigs, setAutoInviteConfigs] = useState([]); // per-brand configs (all brands, one list)

  // Expandable rows: tracks which activity/conversation is expanded to show detail.
  const [expandedRow, setExpandedRow] = useState(null); // activity._id or null

  // Stats drill-down modal: shows the underlying ops for a clicked stat block.
  const [statsDrill, setStatsDrill] = useState(null); // { title, status, op, activities, loading }

  // Open a drill-down modal for a stats block — fetches the matching activities
  // company-wide (all brands the console covers).
  const openStatsDrill = async (status, op, label) => {
    setStatsDrill({ title: `${label} (${status})`, status, op, activities: [], loading: true });
    try {
      const res = await getStatsDetail({ status, op, limit: 100 });
      setStatsDrill((s) => (s ? { ...s, activities: (res && res.data) || [], loading: false } : s));
    } catch (_) {
      setStatsDrill((s) => (s ? { ...s, loading: false } : s));
    }
  };

  // Server-side login is not used — the Chrome extension runs ops locally in the operator's browser.

  // ---- loaders (company-wide — the console spans every brand) -------------
  const loadActivities = useCallback(async () => {
    try {
      const [inv, msg] = await Promise.all([
        getActivities({ type: "invites", subType: inviteSubTab !== "all" ? inviteSubTab : undefined }),
        getActivities({ type: "messages", subType: messageSubTab !== "all" ? messageSubTab : undefined }),
      ]);
      setInvites((inv && (inv.data || inv)) || []);
      setMessages((msg && (msg.data || msg)) || []);
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not load activities.", "danger"); }
  }, [inviteSubTab, messageSubTab]);

  const loadContacts = useCallback(async () => {
    try {
      const res = await getScoredContacts({});
      setContacts((res && (res.data || res)) || []);
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not load contacts.", "danger"); }
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await getConversations({});
      setConversations((res && (res.data || res)) || []);
    } catch (_) { /* best-effort */ }
  }, []);

  // ---- stats + event log loaders -------------------------------------------
  const loadStats = useCallback(async () => {
    try {
      const res = await getConnectorStats({});
      setStats((res && res.data) || null);
    } catch (_) { /* best-effort — keep previous stats */ }
  }, []);

  const loadEvents = useCallback(async (opts = {}) => {
    setLogLoading(true);
    try {
      const params = { limit: 50 };
      if (opts.before) params.before = opts.before;
      if (logFilter !== "all") params.type = logFilter;
      const res = await getConnectorEvents(params);
      const data = (res && res.data) || [];
      if (opts.append) {
        setLogEvents((prev) => [...prev, ...data]);
      } else {
        setLogEvents(data);
      }
      setLogHasMore(!!(res && res.hasMore));
      setLogCursor((res && res.cursor) || null);
    } catch (_) { /* best-effort */ }
    finally { setLogLoading(false); }
  }, [logFilter]);

  // ---- auto-invite: company-wide master + every brand's config in one call --
  const loadAutoInviteAll = useCallback(async () => {
    try {
      const res = await getAutoInviteAll();
      const d = (res && (res.data || res)) || {};
      setAutoInviteMaster(!!d.autoInviteEnabled);
      setAutoInviteConfigs(Array.isArray(d.configs) ? d.configs : []);
    } catch (_) { /* best-effort */ }
  }, []);

  const toggleAutoInviteMaster = async (on) => {
    setAutoInviteMaster(on); // optimistic
    try { await saveConnectorPrefs({ autoInviteEnabled: on }); }
    catch (e) { setAutoInviteMaster(!on); addNotification((e && (e.error || e.message)) || "Could not toggle auto-invite.", "danger"); }
  };

  // Toggle one brand's participation (ConnectorAutoInvite.enabled) from the list.
  const setBrandParticipate = async (brandId, enabled) => {
    setAutoInviteConfigs((cs) => {
      const i = cs.findIndex((c) => String(c.brand_id) === String(brandId));
      if (i < 0) return [...cs, { brand_id: brandId, enabled, platforms: {} }];
      const next = cs.slice(); next[i] = { ...next[i], enabled }; return next;
    });
    try { await saveAutoInviteConfig({ brandId, enabled }); loadAutoInviteAll(); }
    catch (e) { addNotification((e && (e.error || e.message)) || "Could not update brand.", "danger"); loadAutoInviteAll(); }
  };

  // ---- auto-invite loaders (per focused/expanded brand) ------------------
  const loadAutoInvite = useCallback(async () => {
    if (!autoInviteBrand) { setAutoInvite(null); setAutoInviteStatus(null); return; }
    try {
      const [cfg, st] = await Promise.all([
        getAutoInviteConfig({ brandId: autoInviteBrand }).catch(() => null),
        getAutoInviteStatus({ brandId: autoInviteBrand }).catch(() => null),
      ]);
      if (cfg && cfg.data) setAutoInvite(cfg.data); else setAutoInvite(null);
      if (st && st.data) setAutoInviteStatus(st.data); else setAutoInviteStatus(null);
    } catch (_) { /* best-effort */ }
  }, [autoInviteBrand]);

  const saveAutoInvite = async (patch) => {
    if (!autoInviteBrand) return;
    setAutoInviteSaving(true);
    try {
      const res = await saveAutoInviteConfig({ brandId: autoInviteBrand, ...patch });
      if (res && res.data) setAutoInvite(res.data);
      loadAutoInviteAll(); // keep the brand list in sync
      addNotification("Auto-invite config saved.", "success");
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not save.", "danger"); }
    finally { setAutoInviteSaving(false); }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try { await Promise.all([loadActivities(), loadContacts(), loadConversations(), loadStats(), loadEvents(), loadAutoInvite(), loadAutoInviteAll()]); }
    finally { setLoading(false); }
  }, [loadActivities, loadContacts, loadConversations, loadStats, loadEvents, loadAutoInvite, loadAutoInviteAll]);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Prospect states for the Messages/Comments view — only fetched when it is open.
  useEffect(() => { if (tab === "messages") loadSkoolProspects(); }, [tab, loadSkoolProspects]);

  // Auto-refresh stats every 30s (lightweight — just counters).
  useEffect(() => {
    if (statsPollRef.current) { clearInterval(statsPollRef.current); statsPollRef.current = null; }
    statsPollRef.current = setInterval(() => loadStats(), 30000);
    return () => { if (statsPollRef.current) { clearInterval(statsPollRef.current); statsPollRef.current = null; } };
  }, [loadStats]);

  // Auto-refresh events every 15s when the log panel is expanded.
  useEffect(() => {
    if (logPollRef.current) { clearInterval(logPollRef.current); logPollRef.current = null; }
    if (!logExpanded) return;
    logPollRef.current = setInterval(() => loadEvents(), 15000);
    return () => { if (logPollRef.current) { clearInterval(logPollRef.current); logPollRef.current = null; } };
  }, [logExpanded, loadEvents]);

  // Reload events when the filter changes.
  useEffect(() => { loadEvents(); }, [logFilter]); // eslint-disable-line

  // ---- unified session controls: load once, persist on toggle ------------
  useEffect(() => {
    (async () => {
      try {
        const res = await getConnectorPrefs();
        const d = (res && (res.data || res)) || {};
        setPlatformCatalog(Array.isArray(d.allowed) ? d.allowed : []);
        setCheckedPlatforms(Array.isArray(d.checked) ? d.checked : []);
        setCheckedBrands(Array.isArray(d.brandsChecked) ? d.brandsChecked : []); // null → [] = all
      } catch (_) { /* best-effort — leave defaults */ }
      finally { setPrefsReady(true); }
    })();
  }, []);

  // Persist a platform checkbox toggle (optimistic).
  const togglePlatform = async (key) => {
    const next = checkedPlatforms.includes(key)
      ? checkedPlatforms.filter((p) => p !== key)
      : [...checkedPlatforms, key];
    setCheckedPlatforms(next);
    const platformsChecked = {};
    platformCatalog.forEach((p) => { platformsChecked[p] = next.includes(p); });
    try { await saveConnectorPrefs({ platformsChecked }); }
    catch (e) { addNotification((e && (e.error || e.message)) || "Could not save platform selection.", "danger"); }
  };

  // Persist a brand checkbox toggle (optimistic). Empty = all brands.
  const toggleBrand = async (id) => {
    const sid = String(id);
    const next = checkedBrands.includes(sid)
      ? checkedBrands.filter((b) => b !== sid)
      : [...checkedBrands, sid];
    setCheckedBrands(next);
    try { await saveConnectorPrefs({ brandsChecked: next }); }
    catch (e) { addNotification((e && (e.error || e.message)) || "Could not save brand selection.", "danger"); }
  };

  // Auto-invite starts collapsed (""); expanding a brand row loads its config.

  // ---- in-browser agent: token + liveness polling ------------------------
  const loadAgentStatus = useCallback(async () => {
    try {
      // Token + status are company-wide now — no brand param.
      const res = await getAgentStatus({});
      const data = (res && (res.data || res)) || {};
      setAgentOnline(!!data.online);
      setAgentSeen(data.lastSeenAt || null);
    } catch (_) { /* best-effort; leave prior status */ }
  }, []);

  // When the Accounts tab is open: load the token, sessions, then poll
  // status every 30s. Re-runs (and clears the interval) on brand/tab change.
  useEffect(() => {
    if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null; }
    if (tab !== "accounts") { return undefined; }
    let cancelled = false;
    setAgentToken(""); setAgentOnline(false); setAgentSeen(null); setSessions([]);
    (async () => {
      try {
        const [tokenRes, sessRes] = await Promise.all([
          getAgentToken({}).catch(() => ({})),
          getSessions({}).catch(() => ({})),
        ]);
        const tokenData = (tokenRes && (tokenRes.data || tokenRes)) || {};
        const sessData = (sessRes && (sessRes.data || sessRes)) || [];
        if (!cancelled) {
          setAgentToken(tokenData.token || "");
          setSessions(Array.isArray(sessData) ? sessData : []);
        }
      } catch (_) { /* best-effort */ }
    })();
    loadAgentStatus();
    agentPollRef.current = setInterval(loadAgentStatus, 30000);
    return () => {
      cancelled = true;
      if (agentPollRef.current) { clearInterval(agentPollRef.current); agentPollRef.current = null; }
    };
  }, [tab, loadAgentStatus]);

  // Pause/resume a session.
  const doTogglePause = async (sessionId, currentlyPaused) => {
    try {
      await toggleSessionPause(sessionId, !currentlyPaused);
      setSessions((prev) => prev.map((s) => s._id === sessionId ? { ...s, status: currentlyPaused ? 'active' : 'paused' } : s));
      addNotification(currentlyPaused ? 'Session resumed' : 'Session paused', 'success');
    } catch (e) { addNotification((e && (e.error || e.message)) || 'Could not toggle session.', 'danger'); }
  };

  // Bulk cancel across every brand the console covers.
  const doBulkCancel = async (op) => {
    if (!hasContext) return;
    try {
      let cancelled = 0;
      for (const bid of activeBrandIds) {
        const res = await bulkCancelActivities({ brandId: bid, op });
        cancelled += (res && res.cancelled) || 0;
      }
      addNotification(`${cancelled} queued/running ops cancelled.`, 'success');
      loadActivities();
      loadStats();
    } catch (e) { addNotification((e && (e.error || e.message)) || 'Could not cancel.', 'danger'); }
  };

  // Open contact timeline modal.
  const openTimeline = async (contact) => {
    setTimelineContact({ ...contact, activities: [], loading: true });
    try {
      const res = await getContactTimeline({ profileUrl: contact.profileUrl });
      setTimelineContact((prev) => prev ? { ...prev, activities: (res && res.data) || [], loading: false } : prev);
    } catch (_) { setTimelineContact((prev) => prev ? { ...prev, loading: false } : prev); }
  };

  // ---- copy agent token ----------------------------------------------------
  const copyAgentToken = useCallback(() => {
    if (!agentToken) return;
    const done = () => addNotification("Token copied", "success");
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(agentToken).then(done).catch(() => addNotification("Could not copy token.", "danger"));
      } else { done(); }
    } catch (_) { addNotification("Could not copy token.", "danger"); }
  }, [agentToken]);

  // ---- run an operation --------------------------------------------------
  // The manual op is queued under the first active brand as a PLACEHOLDER; the
  // extension re-classifies the target against every checked brand at run time
  // and re-attributes it to the best fit. Platforms come from the global
  // platform checkboxes (checkedPlatforms), not a per-form picker.
  const submitOp = async () => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    if (!checkedPlatforms.length) { addNotification("Check at least one platform above.", "danger"); return; }
    // search_invite drives off a keyword (the extension builds the search URL);
    // every other op needs a concrete target URL (profile / page).
    if (op.op === "search_invite") {
      if (!op.keyword.trim()) { addNotification("A search keyword is required.", "danger"); return; }
    } else if (op.op === "invite_to_page") {
      if (!op.targetUrl.trim()) { addNotification("A target profile URL is required for invite_to_page.", "danger"); return; }
      if (!op.pageUrl.trim()) { addNotification("A page URL is required for invite_to_page.", "danger"); return; }
    } else if (!op.targetUrl.trim()) {
      addNotification("A target URL is required.", "danger"); return;
    }
    // An op runs AS a brand — it uses that brand's connected social account and
    // its persona, and the recipient sees a real invite or DM from it. This used
    // to take activeBrandIds[0], "whichever brand happens to be first", which
    // with no brand checked means the first brand in the company: the op went
    // out under the wrong identity and still reported success. Naming the brand
    // is the only safe form, so refuse rather than guess.
    if (!opBrandId) {
      addNotification(
        activeBrandIds.length > 1
          ? "Choose which brand this runs as — an invite or DM goes out under that brand's account."
          : "Check a brand above first.",
        "danger",
      );
      return;
    }
    setOpBusy(true);
    try {
      const payload = { text: op.text.trim() || undefined, note: op.note.trim() || undefined };
      if (op.op === "search_invite") payload.keyword = op.keyword.trim();
      if ((op.op === "search_invite" || op.op === "invite_to_page") && String(op.maxInvites).trim()) {
        payload.maxInvites = Math.max(1, parseInt(op.maxInvites, 10) || 0);
      }
      if (op.op === "invite_to_page" && op.pageUrl.trim()) {
        payload.pageUrl = op.pageUrl.trim();
      }
      // Targeted ops (a URL points at ONE platform) infer it from the URL;
      // keyword ops fan out to every CHECKED platform — one activity each,
      // the extension runs the right flow per platform.
      const inferPlatform = (u) => (/facebook\.com|fb\.com|messenger\.com/i.test(u) ? "facebook" : /linkedin\.com/i.test(u) ? "linkedin" : null);
      let targets;
      if (op.op === "search_invite") {
        targets = checkedPlatforms.slice();
        if (!targets.length) { addNotification("Check at least one platform above.", "danger"); setOpBusy(false); return; }
      } else {
        const inferred = inferPlatform(op.targetUrl) || inferPlatform(op.pageUrl);
        if (!inferred) { addNotification("Could not tell the platform from the URL — paste a linkedin.com or facebook.com link.", "danger"); setOpBusy(false); return; }
        if (!checkedPlatforms.includes(inferred)) { addNotification(`${PLATFORM_LABEL[inferred] || inferred} is not checked above — check it to run this op.`, "danger"); setOpBusy(false); return; }
        targets = [inferred];
      }
      for (const platform of targets) {
        await runOp({
          brandId: opBrandId,
          platform,
          op: op.op,
          target: { url: op.targetUrl.trim() || undefined, name: op.targetName.trim() || undefined },
          payload,
        });
      }
      addNotification(`${OP_LABEL[op.op] || "Operation"} queued${targets.length > 1 ? ` on ${targets.map((p) => PLATFORM_LABEL[p] || p).join(" + ")}` : ""}.`, "success");
      setOp((o) => ({ ...o, targetUrl: "", targetName: "", text: "", note: "", keyword: "", maxInvites: "", pageUrl: "" }));
      loadActivities();
      loadContacts();
      loadStats(); loadEvents();
    } catch (e) { addNotification((e && (e.error || e.message)) || "Operation failed.", "danger"); }
    finally { setOpBusy(false); }
  };

  // Harvest is a LEARNING op — it always runs regardless of the checkboxes, but
  // we still fan it out over checked brands × checked platforms so the harvested
  // connections are scored against each participating brand.
  const doHarvest = async () => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    const picks = checkedPlatforms.slice();
    if (!picks.length) { addNotification("Check at least one platform above.", "danger"); return; }
    setContactBusy("harvest");
    try {
      for (const bid of activeBrandIds) {
        for (const platform of picks) {
          await harvestContacts({ brandId: bid, platform });
        }
      }
      addNotification(`Harvest queued on ${picks.map((p) => PLATFORM_LABEL[p]).join(" + ")} — the extension will collect your connections.`, "success");
      loadStats(); loadEvents();
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not queue harvest.", "danger"); }
    finally { setContactBusy(""); }
  };
  // Instagram DISCOVERY (followers mode): harvest a SEED account's followers →
  // visit each profile → full bio → classify by brand. The followers of a
  // relevant account (competitor / industry aggregator / influencer) are your
  // in-niche prospects. (Hashtag pages are dead IG-side.)
  const doDiscover = async () => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    const seed = discoverKeyword.trim().replace(/^@/, "");
    if (!seed) { addNotification("Enter a seed Instagram account (e.g. an industry leader) to harvest its followers.", "danger"); return; }
    setContactBusy("discover");
    try {
      for (const bid of activeBrandIds) {
        await discoverContacts({ brandId: bid, platform: "instagram", mode: "followers", seedAccount: seed, maxProfiles: 15 });
      }
      addNotification(`Instagram discovery queued for @${seed}'s followers — the extension will harvest, visit profiles, and score them per brand.`, "success");
      setDiscoverKeyword("");
      loadStats(); loadEvents();
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not queue discovery.", "danger"); }
    finally { setContactBusy(""); }
  };
  const doCategorize = async () => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    setContactBusy("categorize");
    try {
      for (const bid of activeBrandIds) await categorizeContacts({ brandId: bid });
      addNotification("Scoring queued — your contacts are being analyzed against every checked brand. Results appear in the table shortly.", "success");
      setTimeout(loadContacts, 8000);
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not categorize.", "danger"); }
    finally { setContactBusy(""); }
  };
  const doGenerate = async (genOp) => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    setContactBusy(genOp);
    try {
      for (const bid of activeBrandIds) await generateOutreach({ brandId: bid, op: genOp });
      addNotification(`Generating personalized ${genOp === "invite" ? "invites" : "messages"} for every 70%+ contact of each checked brand… they'll appear in ${genOp === "invite" ? "Invites" : "Messages"}.`, "success");
      setTimeout(() => { loadActivities(); loadContacts(); loadStats(); loadEvents(); }, 6000);
    } catch (e) { addNotification((e && (e.error || e.message)) || "Could not generate outreach.", "danger"); }
    finally { setContactBusy(""); }
  };

  // ---- quick invite/message per contact ----------------------------------
  const openQuick = (contact, op) => {
    setQuickModal({ contact, op, text: "", note: "", busy: false });
  };
  const closeQuick = () => setQuickModal(null);
  const submitQuick = async () => {
    if (!quickModal) return;
    const { contact, op, text, note } = quickModal;
    const brandId = brandForContact(contact);
    if (!brandId) {
      addNotification(
        activeBrandIds.length > 1
          ? "This contact has no brand recorded — check exactly one brand above so it is clear who the message comes from."
          : "Check a brand above first.",
        "danger",
      );
      return;
    }
    setQuickModal((m) => (m ? { ...m, busy: true } : m));
    try {
      await runOp({
        brandId,
        platform: contact.platform,
        op,
        target: { url: contact.profileUrl, name: contact.name },
        payload: { text: text.trim() || undefined, note: note.trim() || undefined },
      });
      addNotification(op === "invite" ? `Invite queued for ${contact.name || contact.profileUrl}` : `Message queued for ${contact.name || contact.profileUrl}`, "success");
      setQuickModal(null);
      loadActivities();
      loadContacts();
      loadStats(); loadEvents();
    } catch (e) {
      addNotification((e && (e.error || e.message)) || "Could not queue operation.", "danger");
      setQuickModal((m) => (m ? { ...m, busy: false } : m));
    }
  };
  // "Generate" button in the quick popup — drafts a message/invite note from the
  // brand settings + contact profile + any prior conversation history.
  const doDraft = async () => {
    if (!quickModal) return;
    const { contact, op } = quickModal;
    const brandId = brandForContact(contact);
    if (!brandId) {
      // Silently returning left the Generate button looking broken; and drafting
      // from an arbitrary brand's settings produces confidently off-brand copy.
      addNotification("Pick a single brand above — the draft is written from that brand's voice.", "danger");
      return;
    }
    setQuickModal((m) => (m ? { ...m, drafting: true } : m));
    try {
      const res = await draftMessage({ brandId, op, contact });
      const drafted = (res && res.data && res.data.text) || "";
      if (!drafted) throw new Error("empty draft");
      setQuickModal((m) => (m ? { ...m, drafting: false, ...(op === "invite" ? { note: drafted } : { text: drafted }) } : m));
    } catch (e) {
      addNotification((e && (e.error || e.message)) || "Could not generate a draft.", "danger");
      setQuickModal((m) => (m ? { ...m, drafting: false } : m));
    }
  };

  // ---- retry failed ops ---------------------------------------------------
  const doRetryActivity = async (activityId) => {
    try {
      await retryActivity(activityId);
      addNotification("Retrying — queued for the extension.", "success");
      loadActivities();
      loadStats();
    } catch (e) {
      addNotification(String((e && (e.error && e.error.message || e.message)) || "Retry failed."), "danger");
    }
  };

  const doRetryAll = async (op) => {
    if (!hasContext) { addNotification("Check at least one brand above.", "danger"); return; }
    try {
      let retried = 0;
      for (const bid of activeBrandIds) {
        const res = await retryAllActivities({ brandId: bid, op });
        retried += (res && res.retried) || 0;
      }
      addNotification(`${retried} failed ${op || "ops"} retried — queued for the extension.`, "success");
      loadActivities();
      loadStats();
    } catch (e) {
      addNotification(String((e && (e.error && e.error.message || e.message)) || "Retry failed."), "danger");
    }
  };

  // ---- render ------------------------------------------------------------
  return (
    <div style={{ maxWidth: 960 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h5 style={{ fontWeight: 700, margin: 0 }}>Connectors</h5>
        <button className="btn btn-sm btn-outline-secondary m-0" onClick={loadAll}>Refresh</button>
      </div>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Browser-automation for what the platform APIs don't expose — invite, message, comment, like and page-invite
        across your social accounts. Check the <b>brands</b> and <b>platforms</b> below to include them: a checked brand
        takes part in matching and its content is executed; a checked platform is where actions run. Learning &amp;
        recording keep running regardless. Every invite, message and contact is tracked below.
      </p>

      {/* ---- Unified control surface: brand + platform checkboxes ---- */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 14, padding: "12px 14px", border: "1px solid #e3e6ec", borderRadius: 8, background: "#fff" }}>
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
            Brands <span style={{ fontWeight: 400, textTransform: "none" }}>— checked take part in matching &amp; execution ({checkedBrands.length ? `${activeBrandIds.length} selected` : "all brands"})</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {brandsRedux.length === 0 && <span className="text-muted" style={{ fontSize: "0.8rem" }}>No brands yet.</span>}
            {brandsRedux.map((b) => {
              const id = String(b._id);
              const on = checkedBrands.length === 0 || checkedBrands.includes(id);
              const explicit = checkedBrands.includes(id);
              return (
                <button key={id} type="button" onClick={() => toggleBrand(id)}
                  title={checkedBrands.length === 0 ? "All brands are active — click to narrow to a selection" : (explicit ? "Checked" : "Unchecked")}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 16, cursor: "pointer",
                    border: `1px solid ${on ? "#0b3d91" : "#d0d5dd"}`, background: on ? "#eef3ff" : "#fff",
                    color: on ? "#0b3d91" : "#667085", fontSize: "0.78rem", fontWeight: 600,
                  }}>
                  <i className={`far fa-${explicit ? "check-square" : (checkedBrands.length === 0 ? "check-square" : "square")}`} />
                  {b.name}
                </button>
              );
            })}
            {checkedBrands.length > 0 && (
              <button type="button" className="btn btn-sm btn-link m-0 p-0" style={{ fontSize: "0.74rem" }} onClick={async () => { setCheckedBrands([]); try { await saveConnectorPrefs({ brandsChecked: [] }); } catch (_) {} }}>
                Select all
              </button>
            )}
          </div>
        </div>
        <div style={{ flex: "1 1 320px", minWidth: 280 }}>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
            Platforms <span style={{ fontWeight: 400, textTransform: "none" }}>— checked platforms are where actions run</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {platformCatalog.length === 0 && <span className="text-muted" style={{ fontSize: "0.8rem" }}>No platforms enabled for this account. Ask an admin to enable connector platforms.</span>}
            {platformCatalog.map((p) => {
              const meta = PLATFORM_META[p] || { label: p, icon: "fas fa-globe", color: "#667085" };
              const on = checkedPlatforms.includes(p);
              return (
                <button key={p} type="button" onClick={() => togglePlatform(p)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 16, cursor: "pointer",
                    border: `1px solid ${on ? meta.color : "#d0d5dd"}`, background: on ? "#f5f8ff" : "#fff",
                    color: on ? "#1f2937" : "#667085", fontSize: "0.78rem", fontWeight: 600,
                  }}>
                  <i className={`far fa-${on ? "check-square" : "square"}`} />
                  <i className={meta.icon} style={{ color: on ? meta.color : "#98a2b3" }} />
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ---- Status bar (always visible, even before brand selection) ---- */}
      <style>{`
        @keyframes connPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.15); }
        }
        .conn-status-dot {
          display: inline-block;
          width: 10px; height: 10px;
          border-radius: 50%;
          margin-right: 6px;
          vertical-align: middle;
        }
        .conn-status-dot.online {
          background: #16a34a;
          animation: connPulse 1.8s ease-in-out infinite;
        }
        .conn-status-dot.offline {
          background: #b0b6bf;
        }
        .conn-status-bar {
          display: flex;
          align-items: center;
          gap: 18px;
          padding: 10px 14px;
          margin-bottom: 14px;
          border-radius: 8px;
          background: #f8fafd;
          border: 1px solid #e3e6ec;
          font-size: 0.82rem;
          flex-wrap: wrap;
          min-height: 40px;
        }
        .conn-status-bar .stat {
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }
        .conn-status-bar .stat-label {
          color: #667085;
          font-weight: 500;
        }
        .conn-status-bar .stat-value {
          font-weight: 700;
        }
        .conn-status-bar .stat-value.pending { color: #9a6700; }
        .conn-status-bar .stat-value.failed  { color: #c0392b; }
        .conn-status-bar .stat-value.online  { color: #16a34a; }
        .conn-status-bar .spinner {
          display: inline-block;
          width: 12px; height: 12px;
          border: 2px solid #e3e6ec;
          border-top-color: #0b3d91;
          border-radius: 50%;
          animation: connPulse 1s linear infinite;
        }
      `}</style>
      <div className="conn-status-bar">
        {(
          <>
            <div className="stat">
              <span className={`conn-status-dot ${agentOnline ? "online" : "offline"}`} />
              <span className="stat-label">Agent:</span>
              <span style={{ color: agentOnline ? "#16a34a" : "#667085", fontWeight: 600, fontSize: "0.78rem" }}>
                {agentOnline ? "Online" : "Offline"}
              </span>
              {agentSeen && !agentOnline && (
                <span className="text-muted" style={{ fontSize: "0.72rem" }}>last seen {fmt(agentSeen)}</span>
              )}
            </div>
            <span style={{ color: "#d0d5dd" }}>|</span>
            {stats && (
              <span style={{ fontSize: "0.76rem", color: "#667085" }}>
                {stats.activities.queued} queued · {stats.activities.success} done · {stats.activities.failed} failed
                {stats.successRate != null && <> · {stats.successRate}% success</>}
              </span>
            )}
          </>
        )}
      </div>

      {/* ---- Activity Log panel (right after agent status) ---- */}
      {(
        <div style={{ marginBottom: 12, border: "1px solid #e3e6ec", borderRadius: 8, overflow: "hidden" }}>
          <button
            onClick={() => { setLogExpanded((v) => !v); if (!logExpanded) loadEvents(); }}
            style={{ width: "100%", padding: "8px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", border: "none", background: logExpanded ? "#f7f9ff" : "#fff", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600, color: "#1f2937" }}
          >
            <span>
              <i className={`fas fa-${logExpanded ? "chevron-down" : "chevron-right"} mr-2`} style={{ color: "#0b3d91" }} />
              Activity Log
              {logEvents.length > 0 && <span style={{ marginLeft: 8, fontSize: "0.74rem", color: "#667085", fontWeight: 400 }}>({logEvents.length} events{logHasMore ? "+" : ""})</span>}
            </span>
            <span style={{ fontSize: "0.72rem", color: "#667085", fontWeight: 400 }}>
              {logExpanded ? "Collapse" : "Expand"} · auto-refresh {logExpanded ? "15s" : "off"}
            </span>
          </button>
          {logExpanded && (
            <div style={{ borderTop: "1px solid #e3e6ec" }}>
              <div style={{ display: "flex", gap: 4, padding: "6px 14px", background: "#fafbfc", borderBottom: "1px solid #f0f1f4", flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ fontSize: "0.72rem", color: "#667085", marginRight: 4 }}>Filter:</span>
                {["all", "op_queued", "op_success", "op_failed", "agent_step", "agent_activity_log", "agent_claimed", "agent_pace_wait", "agent_pending_poll", "agent_ping", "harvest_queued", "harvest_completed", "outreach_queued", "extension_online"].map((t) => (
                  <button key={t} className={"btn btn-sm m-0 " + (logFilter === t ? "btn-secondary" : "btn-outline-secondary")}
                    style={{ fontSize: "0.68rem", padding: "1px 7px" }}
                    onClick={() => setLogFilter(t)}>{t === "all" ? "All" : t.replace(/_/g, " ")}</button>
                ))}
              </div>
              <div style={{ maxHeight: 320, overflowY: "auto", background: "#fff" }}>
                {logLoading && logEvents.length === 0 ? (
                  <div style={{ padding: 20, textAlign: "center", color: "#667085", fontSize: "0.82rem" }}>Loading events…</div>
                ) : logEvents.length === 0 ? (
                  <div style={{ padding: 20, textAlign: "center", color: "#667085", fontSize: "0.82rem" }}>No events yet. Trigger an operation to see the log.</div>
                ) : (
                  <>
                    {logEvents.map((ev, i) => (
                      <div key={ev._id || i} style={{ padding: "6px 14px", borderBottom: "1px solid #f5f5f5", display: "flex", alignItems: "flex-start", gap: 10, fontSize: "0.76rem", lineHeight: 1.5 }}>
                        <span style={{ fontSize: "0.66rem", color: "#9ca3af", minWidth: 96, maxWidth: 120, lineHeight: 1.3 }} title={fmt(ev.createdAt)}>{fmt(ev.createdAt)}</span>
                        <span style={{ display: "inline-block", fontSize: "0.64rem", fontWeight: 700, padding: "1px 6px", borderRadius: 8, whiteSpace: "nowrap", minWidth: 60, textAlign: "center",
                          background: ev.type === "agent_pace_wait" ? "#fff4e5" : ev.type.startsWith("op_failed") ? "#fdecea" : ev.type.startsWith("op_success") ? "#e7f5ee" : ev.type === "agent_step" ? "#e0f2fe" : ev.type === "agent_activity_log" ? "#f3e8ff" : ev.type.startsWith("agent") ? "#e7f0ff" : ev.type.startsWith("harvest") ? "#fff4e5" : "#f0f1f4",
                          color: ev.type === "agent_pace_wait" ? "#9a6700" : ev.type.startsWith("op_failed") ? "#c0392b" : ev.type.startsWith("op_success") ? "#1a7f37" : ev.type === "agent_step" ? "#0284c7" : ev.type === "agent_activity_log" ? "#7c3aed" : ev.type.startsWith("agent") ? "#0b3d91" : ev.type.startsWith("harvest") ? "#9a6700" : "#667085",
                        }}>{ev.type.replace(/_/g, " ")}</span>
                        <span style={{ flex: 1 }}>
                          <span style={{ color: ev.type.startsWith("op_failed") ? "#c0392b" : "#1f2937" }}>{ev.summary}</span>
                          {ev.error && <span style={{ color: "#c0392b", marginLeft: 6 }}>— {ev.error}</span>}
                        </span>
                        {ev.durationMs != null && <span style={{ fontSize: "0.66rem", color: "#9ca3af", whiteSpace: "nowrap" }}>{ev.durationMs}ms</span>}
                      </div>
                    ))}
                    {logHasMore && (
                      <button
                        className="btn btn-sm btn-link m-0"
                        style={{ width: "100%", padding: "10px 0", fontSize: "0.78rem" }}
                        onClick={() => loadEvents({ before: logCursor, append: true })}
                      >
                        Load more…
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ---- Automation stats ---- */}
      {stats && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8, padding: "8px 14px", background: "#fff", border: "1px solid #e3e6ec", borderRadius: 8, fontSize: "0.76rem", lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: "#1f2937", marginRight: 8, fontSize: "0.78rem" }}>Automation</span>
          <StatBlock label="Auto invites" value={stats.outreach?.generated || 0} color="#0b3d91" bg="#e7f0ff" />
          <StatBlock label="Auto messages" value={stats.outreach?.generated || 0} color="#1a7f37" bg="#e7f5ee" />
          <StatBlock label="Total threads" value={stats.activities?.success || 0} color="#7c3aed" bg="#f3e8ff" />
          <StatBlock label="Total replies" value={stats.extension?.results || 0} color="#0284c7" bg="#e0f2fe" />
          <span className="text-muted" style={{ fontSize: "0.7rem", marginLeft: 4 }}>
            <i className="fas fa-sync-alt mr-1" style={{ fontSize: "0.65rem" }} />
            Live · updates every 30s
          </span>
        </div>
      )}
      {/* ---- Per-op-type stats ---- */}
      {stats && stats.byOpType && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12, padding: "8px 14px", background: "#fafbfe", border: "1px solid #e3e6ec", borderRadius: 8, fontSize: "0.76rem", lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: "#1f2937", marginRight: 8, fontSize: "0.78rem" }}>By op type</span>
          {Object.entries(stats.byOpType).map(([opName, counts]) => (
            <span key={opName} style={{ display: "inline-flex", alignItems: "center", gap: 2, whiteSpace: "nowrap", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, color: "#1f2937", marginRight: 2 }}>{OP_LABEL[opName] || opName}:</span>
              <span style={{ color: "#9a6700", fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} title="Click to see queued ops" onClick={() => openStatsDrill("queued", opName, OP_LABEL[opName] || opName)}>{counts.queued || 0}Q</span>
              <span style={{ color: "#667085" }}>/</span>
              <span style={{ color: "#1a7f37", fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} title="Click to see successes" onClick={() => openStatsDrill("success", opName, OP_LABEL[opName] || opName)}>{counts.success || 0}S</span>
              <span style={{ color: "#667085" }}>/</span>
              <span style={{ color: "#c0392b", fontWeight: 600, cursor: "pointer", textDecoration: "underline dotted" }} title="Click to see failures" onClick={() => openStatsDrill("failed", opName, OP_LABEL[opName] || opName)}>{counts.failed || 0}F</span>
              <span className="text-muted" style={{ marginLeft: 1 }}>({counts.total || 0})</span>
            </span>
          ))}
        </div>
      )}

      {/* ---- Permanent stats bar ---- */}
      {stats && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 12, padding: "10px 14px", background: "#f7f9ff", border: "1px solid #e3e6ec", borderRadius: 8, fontSize: "0.78rem", lineHeight: 1.6 }}>
          <StatBlock label="Queued" value={stats.activities.queued} color="#9a6700" bg="#fff4e5" onClick={() => openStatsDrill("queued", "", "All ops")} title="Click to see queued ops" />
          <StatBlock label="Running" value={stats.activities.running} color="#0b3d91" bg="#e7f0ff" onClick={() => openStatsDrill("running", "", "All ops")} title="Click to see running ops" />
          <StatBlock label="Succeeded" value={stats.activities.success} color="#1a7f37" bg="#e7f5ee" onClick={() => openStatsDrill("success", "", "All ops")} title="Click to see successes" />
          <StatBlock label="Failed" value={stats.activities.failed} color="#c0392b" bg="#fdecea" onClick={() => openStatsDrill("failed", "", "All ops")} title="Click to see failures" />
          <StatBlock label="Total Ops" value={stats.activities.total} color="#667085" bg="#f0f1f4" />
          <span style={{ width: 1, background: "#d0d5dd", margin: "2px 4px" }} />
          <StatBlock label="Harvested" value={stats.harvest.totalIngested} color="#1a7f37" bg="#e7f5ee" />
          <StatBlock label="Outreach" value={stats.outreach.generated} color="#0b3d91" bg="#e7f0ff" />
          {stats.successRate != null && (
            <StatBlock label="Success Rate" value={`${stats.successRate}%`} color={stats.successRate >= 70 ? "#1a7f37" : "#9a6700"} bg={stats.successRate >= 70 ? "#e7f5ee" : "#fff4e5"} />
          )}
          <span style={{ width: 1, background: "#d0d5dd", margin: "2px 4px" }} />
          <StatBlock label="Ext Pings" value={stats.extension.pings} color="#667085" bg="#f0f1f4" />
          <StatBlock label="Ext Polls" value={stats.extension.polls} color="#667085" bg="#f0f1f4" />
          <span style={{ width: 1, background: "#d0d5dd", margin: "2px 4px" }} />
          {stats.lastEvent && (
            <span style={{ fontSize: "0.74rem", color: "#667085", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 160 }}>
              <b>Last:</b> {fmt(stats.lastEvent.at)} — {stats.lastEvent.summary}
            </span>
          )}
        </div>
      )}

      {/* Sub-tabs (local state, not routes) */}
      <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e5e5e5", flexWrap: "wrap", marginBottom: 16 }}>
        {SUB_TABS.map((t) => {
          const isActive = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{
                padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer",
                fontSize: "0.85rem", fontWeight: 600,
                color: isActive ? "#0b3d91" : "#667085",
                borderBottom: isActive ? "2px solid #0b3d91" : "2px solid transparent", marginBottom: -1,
              }}>
              {t.label}
            </button>
          );
        })}
      </div>


      {/* ---- Accounts (always visible) ---- */}
      {tab === "accounts" && (
            <>
              {/* Skool — a direct JSON-API channel, not browser automation, so it
                  has no streamed login. Its own brand picker: this page has no
                  single "active brand", it works across all of them. */}
              <div style={{ maxWidth: 860, marginBottom: 18 }}>
                <div className="form-group" style={{ maxWidth: 320 }}>
                  <label className="small mb-1">Skool community for brand</label>
                  <select
                    className="form-control form-control-sm"
                    value={skoolBrandId}
                    onChange={(e) => setSkoolBrand(e.target.value)}
                  >
                    {brandsRedux.map((b) => (
                      <option key={b._id} value={b._id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                <SkoolPanel brandId={skoolBrandId} />
              </div>

              {/* In-browser agent (recommended) — runs ops in the operator's own browser */}
              <div style={{ ...card, maxWidth: 560, marginBottom: 18, borderColor: "#0b3d91" }}>
                <div className="d-flex align-items-center justify-content-between mb-1" style={{ gap: 8 }}>
                  <span style={{ fontWeight: 700 }}>
                    <i className="fas fa-bolt" style={{ color: "#0b3d91", marginRight: 8 }} />
                    In-browser agent <span style={{ color: "#1a7f37", fontWeight: 600 }}>(recommended)</span>
                  </span>
                  <span className="d-flex align-items-center" style={{ gap: 6, fontSize: "0.76rem", fontWeight: 600 }}>
                    <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: "50%", background: agentOnline ? "#1a7f37" : "#b0b6bf" }} />
                    {agentOnline ? <span style={{ color: "#1a7f37" }}>Extension online</span>
                      : <span style={{ color: "#667085" }}>Extension offline{agentSeen ? ` · last seen ${fmt(agentSeen)}` : ""}</span>}
                  </span>
                </div>
                <p className="text-muted" style={{ fontSize: "0.82rem", marginBottom: 10 }}>
                  Run invites &amp; messages from your <strong>OWN</strong> browser — no IP blocks, no cookie copying. Install once.
                </p>
                <ol className="text-muted" style={{ fontSize: "0.82rem", marginBottom: 10, paddingLeft: 18, lineHeight: 1.7 }}>
                  <li>
                    Download the extension → {" "}
                    <a href={EXTENSION_ZIP_URL} download style={{ color: "#0b3d91", fontWeight: 600 }}>Download .zip</a>.
                  </li>
                  <li>Open <code>chrome://extensions</code>, turn on <strong>Developer mode</strong>, click <strong>Load unpacked</strong>, select the unzipped folder.</li>
                  <li>Open the extension, paste the token below + this site&rsquo;s URL, then <strong>Save</strong>.</li>
                </ol>
                <label style={{ fontSize: "0.74rem", fontWeight: 600, color: "#667085", marginBottom: 4, display: "block" }}>Agent token (company-wide — one token covers all brands &amp; platforms)</label>
                <div className="d-flex" style={{ gap: 8, marginBottom: 10 }}>
                  <input
                    className="form-control form-control-sm"
                    readOnly
                    value={agentToken || "Loading…"}
                    onFocus={(e) => e.target.select()}
                    style={{ fontFamily: "monospace", fontSize: "0.78rem", background: "#f4f6fa" }}
                  />
                  <button className="btn btn-sm btn-outline-primary m-0" onClick={copyAgentToken} disabled={!agentToken}>Copy</button>
                </div>
                <a className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} href={EXTENSION_ZIP_URL} download>
                  <i className="fas fa-download" style={{ marginRight: 6 }} />Download extension
                </a>
              </div>

              {/* Platform status cards — one per CHECKED platform (extension runs locally) */}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
                {checkedPlatforms.length === 0 && (
                  <span className="text-muted" style={{ fontSize: "0.82rem" }}>Check a platform above to see its status here.</span>
                )}
                {checkedPlatforms.map((key) => {
                  const meta = PLATFORM_META[key] || { label: key, icon: "fas fa-globe", color: "#667085" };
                  const sess = sessions.find((s) => s.platform === key);
                  const isPaused = sess && sess.status === 'paused';
                  return (
                  <div key={key} style={{ ...card, width: 280 }}>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span style={{ fontWeight: 700 }}>
                        <i className={meta.icon} style={{ color: meta.color, marginRight: 8 }} />{meta.label}
                      </span>
                      <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: agentOnline ? "#e7f5ee" : "#eceef1", color: agentOnline ? "#1a7f37" : "#667085" }}>
                        {agentOnline ? "Extension active" : "Install extension"}
                      </span>
                    </div>
                    <p className="text-muted" style={{ fontSize: "0.76rem", lineHeight: 1.5 }}>
                      The extension uses <strong>your own logged-in browser session</strong> — just make sure you're logged into {meta.label} in Chrome.
                      No server-side connection needed.
                    </p>
                    {agentOnline && (
                      <div className="text-muted" style={{ fontSize: "0.72rem" }}>
                        <i className="fas fa-check-circle mr-1" style={{ color: "#1a7f37" }} />
                        Extension polling — ops will run automatically.
                      </div>
                    )}
                    {sess && (
                      <div className="d-flex" style={{ gap: 6, marginTop: 8 }}>
                        {isPaused ? (
                          <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 6, background: "#fff4e5", color: "#9a6700", fontWeight: 600 }}>Paused</span>
                        ) : sess.status === 'active' ? (
                          <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 6, background: "#e7f5ee", color: "#1a7f37", fontWeight: 600 }}>Active</span>
                        ) : sess.status === 'needs_login' ? (
                          <span style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 6, background: "#fdecea", color: "#c0392b", fontWeight: 600 }}>Needs login</span>
                        ) : null}
                        <button className="btn btn-sm m-0" style={{ fontSize: "0.68rem", padding: "1px 7px" }}
                          onClick={() => doTogglePause(sess._id, isPaused)}>
                          {isPaused ? '▶ Resume' : '⏸ Pause'}
                        </button>
                      </div>
                    )}
                  </div>
                );})}
              </div>

              {/* ---- Auto-Invite: company-wide master switch ---- */}
              <div style={{ ...card, background: "#fafbfe", borderColor: "#c8d6e5", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: "0.9rem" }}><i className="fas fa-robot mr-2" style={{ color: "#0b3d91" }} />Auto-Invite</span>
                  <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: autoInviteMaster ? "#e7f5ee" : "#eceef1", color: autoInviteMaster ? "#1a7f37" : "#667085" }}>
                    {autoInviteMaster ? "ON — company-wide" : "OFF"}
                  </span>
                  <label style={{ marginLeft: "auto", fontSize: "0.82rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="checkbox" checked={autoInviteMaster} onChange={(e) => toggleAutoInviteMaster(e.target.checked)} />
                    Enable auto-invite
                  </label>
                </div>
                <p className="text-muted" style={{ fontSize: "0.76rem", margin: "8px 0 0" }}>
                  One switch for the whole company. While ON, the worker harvests and classifies each contact against
                  <b> every participating brand</b> and fires the personalized invite / DM / comment from the brand that
                  <b> best matches</b> that contact — a Prigmar-fit contact goes to Prigmar, an Inferna-fit contact goes to
                  Inferna. No brand picker: check the brands that participate below; each brand's caps apply to the contacts
                  routed to it. Harvesting &amp; learning keep running regardless.
                </p>
              </div>

              {/* ---- Participating brands (one list, no dropdown) ---- */}
              <div style={{ ...card, background: "#fff", borderColor: "#e3e6ec", marginBottom: 8 }}>
                <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#667085", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
                  Participating brands <span style={{ fontWeight: 400, textTransform: "none" }}>— checked brands take part in matching &amp; auto outreach</span>
                </div>
                {brandsRedux.length === 0 && <div className="text-muted" style={{ fontSize: "0.8rem" }}>No brands yet.</div>}
                {brandsRedux.map((b) => {
                  const id = String(b._id);
                  const cfg = autoInviteConfigs.find((c) => String(c.brand_id) === id) || { enabled: false, platforms: {} };
                  const plats = ["linkedin", "facebook", "instagram"].filter((p) => cfg.platforms && cfg.platforms[p] && cfg.platforms[p].enabled);
                  const expanded = autoInviteBrand === id;
                  return (
                    <div key={id} style={{ borderTop: "1px solid #f0f1f4", padding: "8px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, cursor: "pointer", fontWeight: 600, fontSize: "0.84rem" }}>
                          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setBrandParticipate(id, e.target.checked)} />
                          {b.name}
                        </label>
                        {cfg.enabled && (
                          <span style={{ fontSize: "0.72rem", color: plats.length ? "#1a7f37" : "#9a6700" }}>
                            {plats.length ? plats.map((p) => PLATFORM_LABEL[p]).join(" + ") : "no platform enabled — configure"}
                          </span>
                        )}
                        <button className="btn btn-sm btn-link m-0 p-0" style={{ marginLeft: "auto", fontSize: "0.76rem" }}
                          onClick={() => setAutoInviteBrand(expanded ? "" : id)}>
                          {expanded ? "Close" : "Configure caps & pacing"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {autoInvite && autoInviteBrand && (
                <div style={{ ...card, background: "#fafbfe", borderColor: "#c8d6e5" }}>
                  <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                    <i className="fas fa-sliders-h" style={{ color: "#0b3d91" }} />
                    Caps &amp; pacing — {(brandsRedux.find((b) => String(b._id) === String(autoInviteBrand)) || {}).name || autoInviteBrand}
                    <button className="btn btn-sm btn-link m-0 p-0" style={{ marginLeft: "auto", fontSize: "0.76rem" }} onClick={() => setAutoInviteBrand("")}>Close</button>
                  </div>
                  <p className="text-muted" style={{ fontSize: "0.76rem", marginBottom: 10 }}>
                    Per-brand limits for contacts the classifier routes to this brand. Enable a platform, set safe caps for
                    invites &amp; page invites (DMs and comments are uncapped), and tune pacing. The company-wide switch above
                    turns the whole loop on/off; this brand only participates when it's checked in the list.
                  </p>

                  {/* Platform tabs */}
                  <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e3e6ec", marginBottom: 10 }}>
                    {["linkedin", "facebook", "instagram"].map((p) => (
                      <button key={p} onClick={() => setAutoInvitePlatform(p)}
                        style={{
                          padding: "6px 14px", border: "none", background: "transparent", cursor: "pointer",
                          fontSize: "0.8rem", fontWeight: autoInvitePlatform === p ? 700 : 400,
                          color: autoInvitePlatform === p ? "#0b3d91" : "#667085",
                          borderBottom: autoInvitePlatform === p ? "2px solid #0b3d91" : "2px solid transparent", marginBottom: -1,
                        }}>
                        {PLATFORM_LABEL[p] || p}
                      </button>
                    ))}
                  </div>

                  {/* Platform config */}
                  {(() => {
                    const p = autoInvitePlatform;
                    const cfg = (autoInvite.config && autoInvite.config.platforms && autoInvite.config.platforms[p]) || {};
                    const limits = (autoInvite.platformLimits && autoInvite.platformLimits[p]) || {};
                    const st = (autoInviteStatus && autoInviteStatus.platforms && autoInviteStatus.platforms[p]) || {};
                    return (
                      <div>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontSize: "0.82rem", cursor: "pointer" }}>
                          <input type="checkbox" checked={!!cfg.enabled}
                            onChange={() => saveAutoInvite({ platforms: { [p]: { enabled: !cfg.enabled } } })} />
                          Enable {PLATFORM_LABEL[p]} {p === "instagram" ? "automation" : "auto invites"}
                        </label>

                        {cfg.enabled && (
                          <>
                            {/* Op-type checkboxes. Instagram has no invite-to-connect
                                or invite-to-page — account growth = auto-FOLLOW
                                high-fit accounts (they often follow back). */}
                            {p === "instagram" ? (
                              <div style={{ marginBottom: 8 }}>
                                <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                                  <input type="checkbox" checked={!!cfg.inviteToPageEnabled}
                                    onChange={() => saveAutoInvite({ platforms: { [p]: { inviteToPageEnabled: !cfg.inviteToPageEnabled } } })} />
                                  <i className="fas fa-user-plus" style={{ color: "#c13584", fontSize: "0.72rem" }} /> Auto-follow to grow <span className="text-muted" style={{ fontSize: "0.72rem" }}>(follow high-fit accounts; they often follow back)</span>
                                </label>
                                <div className="text-muted" style={{ fontSize: "0.7rem", marginTop: 4 }}>
                                  Instagram has no “invite to page”. The <b>daily cap</b> below limits follows/day. Discovery (mining seed accounts’ followers) + DMs/comments run under the company loop.
                                </div>
                              </div>
                            ) : (
                            <div style={{ display: "flex", gap: 14, marginBottom: 8, flexWrap: "wrap" }}>
                              <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                                <input type="checkbox" checked={!!cfg.inviteEnabled}
                                  onChange={() => saveAutoInvite({ platforms: { [p]: { inviteEnabled: !cfg.inviteEnabled } } })} />
                                <i className="fas fa-user-plus" style={{ color: "#0b3d91", fontSize: "0.72rem" }} /> Invite to connect
                              </label>
                              <label style={{ fontSize: "0.8rem", display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                                <input type="checkbox" checked={!!cfg.inviteToPageEnabled}
                                  onChange={() => saveAutoInvite({ platforms: { [p]: { inviteToPageEnabled: !cfg.inviteToPageEnabled } } })} />
                                <i className="fas fa-bullhorn" style={{ color: "#9a6700", fontSize: "0.72rem" }} /> Invite to page
                              </label>
                            </div>
                            )}

                            {/* Caps */}
                            <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                              {[{ key: "dailyCap", label: "Daily cap" }, { key: "weeklyCap", label: "Weekly cap" }, { key: "monthlyCap", label: "Monthly cap" }].map((f) => (
                                <div key={f.key} style={{ flex: "1 1 100px", minWidth: 80 }}>
                                  <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 600, color: "#667085", marginBottom: 2 }}>{f.label}</label>
                                  <input type="number" min={0} className="form-control form-control-sm"
                                    value={cfg[f.key] || 0}
                                    onChange={(e) => {
                                      const v = Math.max(0, Number(e.target.value) || 0);
                                      saveAutoInvite({ platforms: { [p]: { [f.key]: v } } });
                                    }}
                                    style={{ width: "100%" }} />
                                </div>
                              ))}
                            </div>

                            {/* Usage bar — profile invites */}
                            {st.caps && st.inviteEnabled && (
                              <div style={{ fontSize: "0.73rem", lineHeight: 1.6, marginBottom: 4, padding: "4px 8px", background: "#f7f9ff", borderRadius: 6 }}>
                                <div style={{ fontWeight: 600, color: "#0b3d91", marginBottom: 2, fontSize: "0.72rem" }}>
                                  <i className="fas fa-user-plus mr-1" style={{ fontSize: "0.65rem" }} />Profile invites
                                </div>
                                {[{ period: "daily", label: "Today" }, { period: "weekly", label: "This week" }, { period: "monthly", label: "This month" }].map((per) => {
                                  const cap = st.caps[per.period] || 0;
                                  const used = (st.used && st.used[per.period]) || 0;
                                  const remain = (st.remaining && st.remaining[per.period]) != null ? st.remaining[per.period] : cap - used;
                                  const pct = cap > 0 ? Math.round((used / cap) * 100) : 0;
                                  return (
                                    <div key={per.period} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 1 }}>
                                      <span style={{ minWidth: 60, color: "#667085", fontSize: "0.7rem" }}>{per.label}:</span>
                                      <span style={{ fontWeight: 600, minWidth: 36, fontSize: "0.7rem" }}>{used}/{cap}</span>
                                      <span style={{ flex: "1 1 50px", height: 5, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                                        <span style={{ display: "block", height: "100%", width: Math.min(100, pct) + "%", borderRadius: 3,
                                          background: pct >= 90 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a" }} />
                                      </span>
                                      <span style={{ color: remain === 0 ? "#dc2626" : "#667085", minWidth: 32, fontSize: "0.7rem" }}>{remain} left</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Usage bar — page invites */}
                            {st.caps && st.inviteToPageEnabled && (
                              <div style={{ fontSize: "0.73rem", lineHeight: 1.6, marginBottom: 6, padding: "4px 8px", background: "#fff8f0", borderRadius: 6 }}>
                                <div style={{ fontWeight: 600, color: "#9a6700", marginBottom: 2, fontSize: "0.72rem" }}>
                                  <i className="fas fa-bullhorn mr-1" style={{ fontSize: "0.65rem" }} />Page invites
                                </div>
                                {[{ period: "daily", label: "Today" }, { period: "weekly", label: "This week" }, { period: "monthly", label: "This month" }].map((per) => {
                                  const cap = st.caps[per.period] || 0;
                                  const used = (st.usedByPage && st.usedByPage[per.period]) || 0;
                                  const remain = (st.remainingByPage && st.remainingByPage[per.period]) != null ? st.remainingByPage[per.period] : cap - used;
                                  const pct = cap > 0 ? Math.round((used / cap) * 100) : 0;
                                  return (
                                    <div key={per.period} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 1 }}>
                                      <span style={{ minWidth: 60, color: "#667085", fontSize: "0.7rem" }}>{per.label}:</span>
                                      <span style={{ fontWeight: 600, minWidth: 36, fontSize: "0.7rem" }}>{used}/{cap}</span>
                                      <span style={{ flex: "1 1 50px", height: 5, borderRadius: 3, background: "#e5e7eb", overflow: "hidden" }}>
                                        <span style={{ display: "block", height: "100%", width: Math.min(100, pct) + "%", borderRadius: 3,
                                          background: pct >= 90 ? "#dc2626" : pct >= 70 ? "#f59e0b" : "#16a34a" }} />
                                      </span>
                                      <span style={{ color: remain === 0 ? "#dc2626" : "#667085", minWidth: 32, fontSize: "0.7rem" }}>{remain} left</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Usage — messages + comments (uncapped, informational) */}
                            {(st.messages || st.comments) && (
                              <div style={{ fontSize: "0.73rem", lineHeight: 1.6, marginBottom: 6, padding: "4px 8px", background: "#f0f7ff", borderRadius: 6 }}>
                                {[
                                  { key: "messages", label: "Messages (DMs)", icon: "fa-comment-dots", color: "#0b3d91" },
                                  { key: "comments", label: "Comments", icon: "fa-comments", color: "#5b21b6" },
                                ].map((row) => {
                                  const s = st[row.key] || { daily: 0, weekly: 0, monthly: 0 };
                                  return (
                                    <div key={row.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 1 }}>
                                      <span style={{ fontWeight: 600, color: row.color, minWidth: 110, fontSize: "0.72rem" }}>
                                        <i className={`fas ${row.icon} mr-1`} style={{ fontSize: "0.65rem" }} />{row.label}
                                      </span>
                                      <span style={{ color: "#667085", fontSize: "0.7rem" }}>Today: <b>{s.daily}</b></span>
                                      <span style={{ color: "#667085", fontSize: "0.7rem" }}>This week: <b>{s.weekly}</b></span>
                                      <span style={{ color: "#667085", fontSize: "0.7rem" }}>This month: <b>{s.monthly}</b></span>
                                      <span style={{ color: "#98a2b3", fontSize: "0.68rem", marginLeft: "auto" }}>uncapped</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Info box — platform limits reference */}
                            <div style={{ padding: "8px 10px", borderRadius: 6, background: "#fef9e7", border: "1px solid #f9e79f", fontSize: "0.72rem", lineHeight: 1.6, color: "#7d6608" }}>
                              <div style={{ fontWeight: 700, marginBottom: 2 }}>
                                <i className="fas fa-info-circle mr-1" />{limits.label} Platform Limits
                              </div>
                              <div>Safe daily: <b>{limits.safeDaily}</b> · Safe weekly: <b>{limits.safeWeekly}</b> · Safe monthly: <b>{limits.safeMonthly}</b></div>
                              {limits.maxWeekly && <div>Premium max weekly: <b>{limits.maxWeekly}</b></div>}
                              {limits.note && <div style={{ marginTop: 4 }}>{limits.note}</div>}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* ---- Automation pacing (server defaults when blank) ---- */}
                  <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid #e3e6ec" }}>
                    <div style={{ fontWeight: 600, fontSize: "0.78rem", marginBottom: 4 }}>
                      <i className="fas fa-tachometer-alt mr-1" style={{ color: "#0b3d91" }} />Automation pacing
                      <span className="text-muted ml-2" style={{ fontWeight: 400, fontSize: "0.7rem" }}>blank = server default · applies on the next cycle (~3 min)</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
                      {[
                        { key: "invitesPerCycle", label: "Invites / cycle", ph: "3" },
                        { key: "messagesPerCycle", label: "DMs / cycle", ph: "5" },
                        { key: "commentsPerCycle", label: "Comments / cycle", ph: "3" },
                        { key: "searchMaxInvites", label: "Invites / search", ph: "5" },
                        { key: "searchCooldownMin", label: "Search every (min)", ph: "10" },
                        { key: "harvestCooldownMin", label: "Harvest every (min)", ph: "15" },
                        { key: "minEligible", label: "Low-pool threshold", ph: "10" },
                      ].map((f) => (
                        <label key={f.key} style={{ fontSize: "0.7rem", fontWeight: 400, marginBottom: 0 }}>
                          <span className="text-muted">{f.label}</span>
                          <input type="number" min={0} className="form-control form-control-sm" placeholder={f.ph}
                            style={{ fontSize: "0.75rem", padding: "3px 6px", height: 26 }}
                            value={(autoInvite.config && autoInvite.config.pacing && autoInvite.config.pacing[f.key]) ?? ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setAutoInvite((ai) => ({ ...ai, config: { ...ai.config, pacing: { ...(ai.config.pacing || {}), [f.key]: v === "" ? null : Number(v) } } }));
                            }} />
                        </label>
                      ))}
                    </div>
                    <button className="btn btn-sm btn-outline-primary m-0 mt-2" style={{ fontSize: "0.72rem", padding: "3px 10px" }}
                      disabled={autoInviteSaving}
                      onClick={() => saveAutoInvite({ pacing: (autoInvite.config && autoInvite.config.pacing) || {} })}>
                      Save pacing
                    </button>
                  </div>

                  {/* Autonomous Instagram discovery seeds */}
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid #e6e9f2" }}>
                    <div style={{ fontWeight: 600, fontSize: "0.78rem", marginBottom: 4 }}>
                      Instagram discovery seeds
                      <span className="text-muted ml-2" style={{ fontWeight: 400, fontSize: "0.7rem" }}>autonomous — mines these accounts' followers when the pool runs dry</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: "0.7rem", marginBottom: 6 }}>
                      One @handle per line — industry leaders / competitors whose followers match your ICP. (Hashtag pages no longer work on Instagram, so discovery mines real accounts' followers.)
                    </div>
                    <textarea className="form-control form-control-sm" rows={3}
                      style={{ fontSize: "0.75rem", padding: "5px 8px" }}
                      placeholder={"@industry_leader\n@top_competitor"}
                      value={(autoInvite.config && Array.isArray(autoInvite.config.discoverySeeds) ? autoInvite.config.discoverySeeds.map((s) => "@" + s).join("\n") : "")}
                      onChange={(e) => {
                        const seeds = e.target.value.split(/[\n,]/).map((s) => s.trim().replace(/^@/, "")).filter(Boolean);
                        setAutoInvite((ai) => ({ ...ai, config: { ...ai.config, discoverySeeds: seeds } }));
                      }} />
                    <button className="btn btn-sm btn-outline-primary m-0 mt-2" style={{ fontSize: "0.72rem", padding: "3px 10px" }}
                      disabled={autoInviteSaving}
                      onClick={() => saveAutoInvite({ discoverySeeds: (autoInvite.config && autoInvite.config.discoverySeeds) || [] })}>
                      Save seeds
                    </button>

                    {/* Auto-derived seeds (read-only): resolved from this brand's SEO
                        keywords by the LLM, used ONLY when no manual seeds are set. */}
                    {autoInvite.config && Array.isArray(autoInvite.config.resolvedSeeds) && autoInvite.config.resolvedSeeds.length > 0 && (
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed #e6e9f2" }}>
                        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#5a6b8c" }}>
                          Auto-derived from SEO keywords
                          <span className="text-muted ml-2" style={{ fontWeight: 400, fontSize: "0.68rem" }}>
                            read-only · used when no manual seeds above
                            {autoInvite.config.resolvedSeedsAt ? ` · refreshed ${new Date(autoInvite.config.resolvedSeedsAt).toLocaleDateString()}` : ""}
                          </span>
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                          {autoInvite.config.resolvedSeeds.map((s) => (
                            <a key={s} href={`https://instagram.com/${s}`} target="_blank" rel="noopener noreferrer"
                              title="Open on Instagram to verify"
                              style={{ fontSize: "0.72rem", padding: "2px 8px", borderRadius: 10, background: "#eef2fb", color: "#3a4a6b", textDecoration: "none", border: "1px solid #dbe3f4" }}>
                              @{s}
                            </a>
                          ))}
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.66rem", marginTop: 5 }}>
                          Click a handle to verify it on Instagram. To override, add manual seeds above (they take priority).
                        </div>
                      </div>
                    )}
                  </div>

                  {autoInviteSaving && <span className="text-muted ml-2" style={{ fontSize: "0.72rem" }}>Saving…</span>}
                </div>
              )}
              {/* Run an operation */}
              <div style={{ ...card, marginTop: 18, background: "#f7f9ff", maxWidth: 560 }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>Run an operation</div>
                <div className="text-muted" style={{ fontSize: "0.72rem", marginBottom: 8 }}>
                  {op.op === "search_invite"
                    ? <>Runs on the platforms checked above: <b>{checkedPlatforms.map((p) => PLATFORM_LABEL[p] || p).join(", ") || "none"}</b>.</>
                    : <>Platform is auto-detected from the URL (and must be checked above).</>}
                </div>
                {/* Which identity the op goes out under. Never inferred: the
                    recipient sees a real invite or DM from this brand. */}
                <div className="form-group mb-2">
                  <label className="small mb-1">Runs as</label>
                  {activeBrandIds.length === 1 ? (
                    <div className="small">
                      <b>{(brandsRedux.find((b) => String(b._id) === String(opBrandId)) || {}).name || opBrandId}</b>
                    </div>
                  ) : (
                    <select className="form-control form-control-sm" value={opBrand} onChange={(e) => setOpBrand(e.target.value)}>
                      <option value="">— choose a brand —</option>
                      {brandsRedux.filter((b) => activeBrandIds.includes(String(b._id))).map((b) => (
                        <option key={b._id} value={String(b._id)}>{b.name}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="d-flex" style={{ gap: 8, marginBottom: 8, alignItems: "center" }}>
                  <select className="form-control form-control-sm" value={op.op} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, op: v })); }}>
                    <optgroup label="— Profile actions —">
                      <option value="invite">Invite (connect/friend request)</option>
                      <option value="message">Message (DM)</option>
                      <option value="comment">Comment (AI-generated, post-aware)</option>
                    </optgroup>
                    <optgroup label="— Page actions —">
                      <option value="follow_page">Follow page</option>
                      <option value="invite_to_page">Invite to page</option>
                    </optgroup>
                    <optgroup label="— Search —">
                      <option value="search_invite">Search & invite</option>
                    </optgroup>
                  </select>
                </div>
                {op.op === "search_invite" ? (
                  <input className="form-control form-control-sm mb-2" placeholder="Search keyword (e.g. 'marketing manager london')"
                    value={op.keyword} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, keyword: v })); }} />
                ) : (
                  <input className="form-control form-control-sm mb-2"
                    placeholder="Target profile URL"
                    value={op.targetUrl} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, targetUrl: v })); }} />
                )}
                {op.op === "invite_to_page" && (
                  <input className="form-control form-control-sm mb-2" placeholder="Page URL (e.g. https://www.linkedin.com/company/...) — where you admin the page"
                    value={op.pageUrl} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, pageUrl: v })); }} />
                )}
                <input className="form-control form-control-sm mb-2" placeholder="Target name (optional)"
                  value={op.targetName} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, targetName: v })); }} />
                {(op.op === "search_invite" || op.op === "invite_to_page") && (
                  <input type="number" min={1} className="form-control form-control-sm mb-2" placeholder={`Max invites (default ${op.op === "search_invite" ? 10 : 20})`}
                    value={op.maxInvites} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, maxInvites: v })); }} />
                )}
                {op.op === "message" ? (
                  <textarea className="form-control form-control-sm mb-2" rows={3} placeholder="Message text"
                    value={op.text} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, text: v })); }} />
                ) : op.op === "comment" ? (
                  <textarea className="form-control form-control-sm mb-2" rows={2} placeholder="Custom comment (optional — leave empty for AI to generate based on the post)"
                    value={op.text} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, text: v })); }} />
                ) : op.op === "follow_page" ? null : (
                  <textarea className="form-control form-control-sm mb-2" rows={2} placeholder="Invite note (optional)"
                    value={op.note} onChange={(e) => { const v = e.target.value; setOp((o) => ({ ...o, note: v })); }} />
                )}
                <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }}
                  disabled={opBusy || !opBrandId || (op.op === "search_invite" ? !op.keyword.trim() : op.op === "invite_to_page" ? !op.targetUrl.trim() || !op.pageUrl.trim() : !op.targetUrl.trim())} onClick={submitOp}>
                  {opBusy ? "Queuing…" : `Queue ${OP_LABEL[op.op] || "op"}`}
                </button>
              </div>

            </>
          )}

          {/* ---- Contacts ---- */}
          {tab === "contacts" && (
            <>
              <div style={{ ...card, background: "#f7f9ff", marginBottom: 14 }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Build a targeted audience</div>
                <div className="text-muted" style={{ fontSize: "0.82rem", marginBottom: 10 }}>
                  1. Harvest your connections → 2. Categorize them against every checked brand → 3. Generate personalized invites/messages for the best-fit people only.
                  Runs across <b>{checkedBrands.length ? `${activeBrandIds.length} checked brand(s)` : "all brands"}</b> on <b>{checkedPlatforms.map((p) => PLATFORM_LABEL[p] || p).join(", ") || "no platforms — check some above"}</b>.
                </div>
                <div className="d-flex" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button className="btn btn-sm btn-outline-primary m-0" disabled={!!contactBusy} onClick={doHarvest}>{contactBusy === "harvest" ? "Queuing…" : "Harvest contacts"}</button>
                  <button className="btn btn-sm btn-outline-primary m-0" disabled={!!contactBusy} onClick={doCategorize}>{contactBusy === "categorize" ? "Scoring…" : "Categorize"}</button>
                  <span style={{ width: 1, height: 22, background: "#d0d5dd", margin: "0 2px" }} />
                  <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} disabled={!!contactBusy} onClick={() => doGenerate("invite")}>{contactBusy === "invite" ? "Generating…" : "Invite (70%+ match)"}</button>
                  <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }} disabled={!!contactBusy} onClick={() => doGenerate("message")}>{contactBusy === "message" ? "Generating…" : "Message (70%+ match)"}</button>
                </div>
                {/* Instagram discovery: find NEW accounts by name/industry (Harvest reads
                    your own followers; Discover finds strangers who match the brand). */}
                <div className="d-flex" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
                  <span className="text-muted" style={{ fontSize: "0.8rem" }}>Discover on Instagram — harvest followers of:</span>
                  <input
                    className="form-control form-control-sm m-0"
                    style={{ maxWidth: 220 }}
                    placeholder="@seed_account (an industry leader)"
                    value={discoverKeyword}
                    onChange={(e) => setDiscoverKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !contactBusy) doDiscover(); }}
                    disabled={!!contactBusy}
                  />
                  <button className="btn btn-sm btn-outline-primary m-0" disabled={!!contactBusy || !discoverKeyword.trim()} onClick={doDiscover}>
                    {contactBusy === "discover" ? "Queuing…" : "Discover followers"}
                  </button>
                  <span className="text-muted" style={{ fontSize: "0.74rem" }}>harvests their followers, visits profiles, scores per brand</span>
                </div>
              </div>

              <div className="d-flex" style={{ gap: 6, marginBottom: 8, alignItems: "center" }}>
                <span className="text-muted" style={{ fontSize: "0.78rem" }}>Filter:</span>
                {["all", "high", "medium", "low", "unscored"].map((t) => (
                  <button key={t} className={"btn btn-sm m-0 " + (tierFilter === t ? "btn-secondary" : "btn-outline-secondary")} style={{ fontSize: "0.72rem", padding: "2px 8px" }} onClick={() => setTierFilter(t)}>{t}</button>
                ))}
                <span className="text-muted" style={{ fontSize: "0.74rem", marginLeft: "auto" }}>{contacts.length} contacts</span>
              </div>

              {!contacts.length ? (
                <div className="text-muted">No contacts yet — click <strong>Harvest contacts</strong> (the extension must be online and you must be logged into the platform in that browser).</div>
              ) : (
                <table className="table table-sm" style={{ fontSize: "0.82rem" }}>
                  <thead><tr><th>Name</th><th>Headline / Company</th><th>Platform</th><th>Brand</th><th>Relevance</th><th>Why</th><th>Reached</th><th></th></tr></thead>
                  <tbody>
                    {contacts.filter((c) => (tierFilter === "all" ? true : tierFilter === "unscored" ? !c.tier : c.tier === tierFilter)).map((c) => (
                      <tr key={c._id || c.profileUrl}>
                        <td>{c.profileUrl ? <a href={c.profileUrl} target="_blank" rel="noreferrer">{c.name || c.profileUrl}</a> : (c.name || "—")}</td>
                        <td className="text-muted">{truncate(c.headline || c.employer || "", 48)}</td>
                        <td>{PLATFORM_LABEL[c.platform] || c.platform}</td>
                        <td style={{ maxWidth: 80, fontSize: "0.74rem" }}>{brandsRedux.find((b) => String(b._id) === String(c.assignedBrandId))?.name || (c.assignedBrandId ? truncate(c.assignedBrandId, 12) : "—")}</td>
                        <td><TierBadge tier={c.tier} score={c.score} /></td>
                        <td className="text-muted">{truncate(c.reason || "", 40)}</td>
                        <td>{c.skipped
                          ? <span className="badge badge-secondary" style={{ fontSize: "0.68rem" }} title={c.skipReason || "Profile classified not relevant to the brand"}>Skipped</span>
                          : c.contacted ? <StatusBadge status="success" /> : <span className="text-muted" style={{ fontSize: "0.74rem" }}>—</span>}</td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <button
                            className="btn btn-sm btn-outline-primary m-0 mr-1"
                            style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                            title={`Invite ${c.name || ""} on ${PLATFORM_LABEL[c.platform] || c.platform}`}
                            onClick={() => openQuick(c, "invite")}
                          >Invite</button>
                          <button
                            className="btn btn-sm btn-outline-secondary m-0"
                            style={{ fontSize: "0.7rem", padding: "2px 6px" }}
                            title={`Message ${c.name || ""} on ${PLATFORM_LABEL[c.platform] || c.platform}`}
                            onClick={() => openQuick(c, "message")}
                          >Message</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

      {/* ---- Invites (brand required) ---- */}
      {tab === "skool" && (
        <>
          {/* This tab had NO picker at all, so it showed whichever brand
              happened to be first with no way to change it from here. */}
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select
              className="form-control form-control-sm"
              value={skoolBrandId}
              onChange={(e) => setSkoolBrand(e.target.value)}
            >
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolConversations brandId={skoolBrandId} />
        </>
      )}

      {/* ---- Skool prospect states (brand required) ---- */}
      {tab === "skoolProspects" && (
        <>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select
              className="form-control form-control-sm"
              value={skoolBrandId}
              onChange={(e) => setSkoolBrand(e.target.value)}
            >
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolProspects brandId={skoolBrandId} />
        </>
      )}

      {/* ---- Skool mentions (brand required) ---- */}
      {tab === "skoolMentions" && (
        <>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select className="form-control form-control-sm" value={skoolBrandId} onChange={(e) => setSkoolBrand(e.target.value)}>
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolMentions brandId={skoolBrandId} />
        </>
      )}

      {/* ---- Skool welcome comments (brand required) ---- */}
      {tab === "skoolWelcomes" && (
        <>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select className="form-control form-control-sm" value={skoolBrandId} onChange={(e) => setSkoolBrand(e.target.value)}>
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolWelcomes brandId={skoolBrandId} />
        </>
      )}

      {/* ---- Skool onboarding ladder (brand required) ---- */}
      {tab === "skoolOnboarding" && (
        <>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select className="form-control form-control-sm" value={skoolBrandId} onChange={(e) => setSkoolBrand(e.target.value)}>
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolOnboarding brandId={skoolBrandId} />
        </>
      )}

      {/* ---- Skool nudges (brand required) ---- */}
      {tab === "skoolNudges" && (
        <>
          <div className="form-group" style={{ maxWidth: 320 }}>
            <label className="small mb-1">Brand</label>
            <select className="form-control form-control-sm" value={skoolBrandId} onChange={(e) => setSkoolBrand(e.target.value)}>
              {brandsRedux.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.name}{skoolBoundBrands.includes(String(b._id)) ? " — Skool connected" : ""}
                </option>
              ))}
            </select>
          </div>
          <SkoolNudges brandId={skoolBrandId} />
        </>
      )}

      {tab === "invites" && (
            <>
              {/* Invite sub-type tabs */}
              <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e3e6ec", marginBottom: 10 }}>
                {INVITE_SUB_TABS.map((st) => (
                  <button key={st.key} onClick={() => setInviteSubTab(st.key)}
                    style={{
                      padding: "4px 12px", border: "none", background: "transparent", cursor: "pointer",
                      fontSize: "0.78rem", fontWeight: inviteSubTab === st.key ? 700 : 400,
                      color: inviteSubTab === st.key ? "#0b3d91" : "#667085",
                      borderBottom: inviteSubTab === st.key ? "2px solid #0b3d91" : "2px solid transparent", marginBottom: -1,
                    }}>
                    {st.label}
                  </button>
                ))}
              </div>
              {invites.some((a) => a.status === "failed") && (
                <div className="mb-2">
                  <button className="btn btn-sm btn-outline-warning m-0" onClick={() => doRetryAll("invite")}>
                    <i className="fas fa-redo mr-1" /> Retry all failed invites
                  </button>
                </div>
              )}
              {(invites.some((a) => a.status === "queued" || a.status === "running")) && (
                <div className="mb-2">
                  <button className="btn btn-sm btn-outline-secondary m-0" onClick={() => doBulkCancel("invite")}
                    style={{ color: "#c0392b", borderColor: "#fdecea" }}>
                    <i className="fas fa-ban mr-1" /> Cancel queued/running invites
                  </button>
                </div>
              )}
              {!invites.length && !conversations.filter((c) => c.startedBy === "outreach").length ? (
              <div className="text-muted">No invites yet. Queue an invite from the Contacts tab.</div>
            ) : (
              <table className="table table-sm" style={{ fontSize: "0.82rem" }}>
                <thead><tr><th>Date</th><th>Platform</th><th>Type</th><th>Brand</th><th>Contact</th><th>Detail</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {invites.map((a) => {
                    const brand = brandsRedux.find((b) => String(b._id) === String(a.brand_id));
                    const isExpanded = expandedRow === a._id;
                    const note = a.payload && a.payload.note;
                    const resultAction = a.result && (a.result.action || a.result.status);
                    const resultText = a.result && (a.result.text || a.result.error || "");
                    const inviteText = a.payload && a.payload.text;
                    return (
                      <React.Fragment key={a._id}>
                        <tr onClick={() => setExpandedRow(isExpanded ? null : a._id)} style={{ cursor: "pointer", background: isExpanded ? "#f7f9ff" : "transparent" }}>
                          <td style={{ whiteSpace: "nowrap" }}>{fmt(a.createdAt)}</td>
                          <td>{PLATFORM_LABEL[a.platform] || a.platform}</td>
                          <td><span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: 6, background: "#f0f1f4", color: "#667085", whiteSpace: "nowrap" }}>{OP_LABEL[a.op] || a.op}</span></td>
                          <td style={{ maxWidth: 80 }}>{brand ? brand.name : (a.brand_id ? truncate(a.brand_id, 12) : "—")}</td>
                          <td>{a.target && a.target.url
                            ? <><a href={a.target.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{(a.target && a.target.name) || truncate(a.target.url, 30)}</a>
                              <button className="btn btn-link btn-sm p-0 m-0 ml-1" style={{ fontSize: "0.65rem", color: "#667085", textDecoration: "underline dotted" }}
                                onClick={(e) => { e.stopPropagation(); openTimeline({ name: (a.target && a.target.name) || '', profileUrl: (a.target && a.target.url) || '', platform: a.platform }); }}
                                title="View timeline">⏱</button></>
                            : (a.target && a.target.name) || "—"}</td>
                          <td className="text-muted" style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {note && <><i className="fas fa-sticky-note mr-1" style={{ color: "#9a6700" }} />{truncate(note, 30)}</>}
                            {resultAction && <span style={{ fontSize: "0.7rem", color: resultAction.includes("already") ? "#9a6700" : "#1a7f37", fontWeight: 500 }}>{resultAction.replace(/_/g, " ")}</span>}
                          </td>
                          <td><StatusBadge status={a.status} /></td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {a.status === "failed" && (
                              <button className="btn btn-link btn-sm p-0 m-0" style={{ fontSize: "0.72rem" }} onClick={(e) => { e.stopPropagation(); doRetryActivity(a._id); }}>Retry</button>
                            )}
                            {isExpanded ? <span className="text-muted ml-1" style={{ fontSize: "0.7rem" }}>▲</span> : <span className="text-muted ml-1" style={{ fontSize: "0.7rem" }}>▼</span>}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr key={`${a._id}-detail`} style={{ background: "#fafbfe" }}>
                            <td colSpan={8} style={{ padding: "8px 14px", borderBottom: "1px solid #e3e6ec" }}>
                              <div style={{ fontSize: "0.78rem", lineHeight: 1.7, display: "flex", flexDirection: "column", gap: 2 }}>
                                {note && <div><span style={{ fontWeight: 600, color: "#667085" }}>Note:</span> <span>{note}</span></div>}
                                {inviteText && <div><span style={{ fontWeight: 600, color: "#667085" }}>Text:</span> <span>{inviteText}</span></div>}
                                {resultAction && <div><span style={{ fontWeight: 600, color: "#667085" }}>Result:</span> <span style={{ color: resultAction.includes("already") ? "#9a6700" : "#1a7f37" }}>{resultAction.replace(/_/g, " ")}</span></div>}
                                {resultText && <div><span style={{ fontWeight: 600, color: "#667085" }}>Detail:</span> <span className="text-muted">{resultText}</span></div>}
                                {a.error && <div><span style={{ fontWeight: 600, color: "#c0392b" }}>Error:</span> <span style={{ color: "#c0392b" }}>{a.error}</span></div>}
                                {a.payload && a.payload.keyword && <div><span style={{ fontWeight: 600, color: "#667085" }}>Keyword:</span> <span>{a.payload.keyword}</span></div>}
                                {a.payload && a.payload.maxInvites && <div><span style={{ fontWeight: 600, color: "#667085" }}>Max invites:</span> <span>{a.payload.maxInvites}</span></div>}
                                {a.payload && a.payload.pageUrl && <div><span style={{ fontWeight: 600, color: "#667085" }}>Page URL:</span> <span>{a.payload.pageUrl}</span></div>}
                                {a.payload && a.payload.names && <div><span style={{ fontWeight: 600, color: "#667085" }}>Filtered names:</span> <span>{a.payload.names.join(", ")}</span></div>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {conversations.filter((c) => c.startedBy === "outreach").map((c) => (
                    <tr key={c._id} style={{ background: "#fafdf7", cursor: "pointer" }}
                      onClick={async () => {
                        setConvSidebar({ conversation: c, thread: c.thread || [], loading: !c.thread?.length });
                        if (!c.thread?.length) {
                          try { const r = await getConversation(c._id); if (r?.data) setConvSidebar({ conversation: r.data, thread: r.data.thread || [] }); }
                          catch (_) {}
                        }
                      }}>
                      <td style={{ whiteSpace: "nowrap" }}>{fmt(c.createdAt)}</td>
                      <td>{PLATFORM_LABEL[c.platform] || c.platform}</td>
                      <td><span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: 6, background: "#e7f0ff", color: "#0b3d91", whiteSpace: "nowrap" }}>Thread</span></td>
                      <td style={{ maxWidth: 80 }}>{c.brandName || "—"}</td>
                      <td>{c.contact && c.contact.profileUrl
                        ? <a href={c.contact.profileUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{c.contact.name || c.contact.profileUrl}</a>
                        : (c.contact && c.contact.name) || "—"}</td>
                      <td style={{ fontSize: "0.7rem", color: "#667085" }}><i className="fas fa-comment mr-1" />{c.messageCount} msgs</td>
                      <td><StatusBadge status={c.status === "converted" ? "success" : "active"} /></td>
                      <td style={{ fontSize: "0.7rem" }}>{c.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </>
          )}

      {/* ---- Messages (brand required) ---- */}
      {tab === "messages" && (
            <>
              {/* Message sub-type tabs */}
              <div style={{ display: "flex", gap: 2, borderBottom: "1px solid #e3e6ec", marginBottom: 10 }}>
                {MESSAGE_SUB_TABS.map((st) => (
                  <button key={st.key} onClick={() => setMessageSubTab(st.key)}
                    style={{
                      padding: "4px 12px", border: "none", background: "transparent", cursor: "pointer",
                      fontSize: "0.78rem", fontWeight: messageSubTab === st.key ? 700 : 400,
                      color: messageSubTab === st.key ? "#0b3d91" : "#667085",
                      borderBottom: messageSubTab === st.key ? "2px solid #0b3d91" : "2px solid transparent", marginBottom: -1,
                    }}>
                    {st.label}
                  </button>
                ))}
              </div>
              {messages.some((a) => a.status === "failed") && (
                <div className="mb-2">
                  <button className="btn btn-sm btn-outline-warning m-0" onClick={() => doRetryAll("message")}>
                    <i className="fas fa-redo mr-1" /> Retry all failed messages
                  </button>
                </div>
              )}
              {(messages.some((a) => a.status === "queued" || a.status === "running")) && (
                <div className="mb-2">
                  <button className="btn btn-sm btn-outline-secondary m-0" onClick={() => doBulkCancel("message")}
                    style={{ color: "#c0392b", borderColor: "#fdecea" }}>
                    <i className="fas fa-ban mr-1" /> Cancel queued/running messages
                  </button>
                </div>
              )}
              {!conversations.length && !messages.length ? (
              <div className="text-muted">No conversations yet. Send a message from the Contacts tab or Run an operation.</div>
            ) : (
              <div>
                {/* Conversation threads - click to open sidebar */}
                {conversations.map((c) => (
                  <div key={c._id} style={{ ...card, marginBottom: 12, cursor: "pointer" }}
                    onClick={() => setConvSidebar({ conversation: c, thread: c.thread || [] })}>
                    <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 8, flexWrap: "wrap" }}>
                      <div>
                        <span style={{ fontWeight: 700 }}>{c.contact && c.contact.name || "Unknown"}</span>
                        <span className="text-muted" style={{ fontSize: "0.76rem", marginLeft: 8 }}>
                          {PLATFORM_LABEL[c.platform] || c.platform} · {c.brandName || "—"}
                        </span>
                      </div>
                      <div className="d-flex" style={{ gap: 6, alignItems: "center" }}>
                        {c.conversionGoal !== "other" && (
                          <span style={{ fontSize: "0.68rem", padding: "2px 7px", borderRadius: 8, background: "#e7f0ff", color: "#0b3d91", fontWeight: 600 }}>
                            Goal: {c.conversionGoal.replace(/_/g, " ")}
                          </span>
                        )}
                        <StatusBadge status={c.status === "converted" ? "success" : c.status} />
                      </div>
                    </div>
                    <div style={{ maxHeight: 100, overflow: "hidden", marginBottom: 6, padding: "4px 0", borderTop: "1px solid #f0f1f4", fontSize: "0.74rem", color: "#667085" }}>
                      {(c.thread || []).slice(-2).map((m, i) => (
                        <div key={i} style={{ padding: "2px 0", display: "flex", gap: 6 }}>
                          <span style={{ fontWeight: 600, minWidth: 30, color: m.direction === "out" ? "#0b3d91" : "#1a7f37" }}>{m.direction === "out" ? "You" : "Them"}</span>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{truncate(m.text, 60)}</span>
                        </div>
                      ))}
                      {(c.thread || []).length === 0 && <div className="text-muted">No messages yet.</div>}
                    </div>
                    <div style={{ fontSize: "0.68rem", color: "#9ca3af" }}>
                      <i className="fas fa-comment mr-1" />{c.messageCount} msgs · Click to view full thread
                    </div>
                  </div>
                ))}
                {/* Activity rows (no conversation yet) - expandable with detail */}
                {messages.filter((a) => !conversations.some((c) => c.lastActivityId === a._id)).map((a) => {
                  const brand = brandsRedux.find((b) => String(b._id) === String(a.brand_id));
                  const isExpanded = expandedRow === a._id;
                  // Skool ops carry their body as payload.content, not payload.text —
                  // every Skool comment row used to show an empty preview.
                  const msgText = a.payload && (a.payload.text || a.payload.content);
                  const prospect = prospectForActivity(a);
                  const pc = prospect ? (PROSPECT[prospect.state] || PROSPECT.new) : null;
                  const resultAction = a.result && (a.result.action || a.result.status);
                  const resultText = a.result && (a.result.text || a.result.error || "");
                  return (
                    <React.Fragment key={a._id}>
                      <div key={a._id} style={{ ...card, marginBottom: 8, opacity: isExpanded ? 1 : 0.7, cursor: "pointer" }}
                        onClick={() => setExpandedRow(isExpanded ? null : a._id)}>
                        <div className="d-flex" style={{ gap: 8, fontSize: "0.8rem", flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ whiteSpace: "nowrap" }}>{fmt(a.createdAt)}</span>
                          <span>{PLATFORM_LABEL[a.platform] || a.platform}</span>
                          <span style={{ fontSize: "0.7rem", padding: "1px 6px", borderRadius: 6, background: "#f0f1f4", color: "#667085" }}>{OP_LABEL[a.op] || a.op}</span>
                          <span style={{ fontWeight: 600 }}>{brand ? brand.name : "—"}</span>
                          <span>{a.target && a.target.name || "—"}</span>
                          {pc && (
                            <span data-testid="comment-prospect-pill" title={`${pc.label} (${PROSPECT_SOURCE[prospect.stateSource] || prospect.stateSource})${prospect.reason ? ` — ${prospect.reason}` : ""}`}
                              style={{ fontSize: "0.66rem", fontWeight: 600, padding: "1px 7px", borderRadius: 10, background: pc.bg, color: pc.color }}>
                              {pc.label}{prospect.stateSource === "manual" ? " ✎" : ""}
                            </span>
                          )}
                          <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{truncate(msgText || "", 60)}</span>
                          <StatusBadge status={a.status} />
                          {resultAction && <span style={{ fontSize: "0.68rem", color: resultAction.includes("already") ? "#9a6700" : "#1a7f37" }}>{resultAction.replace(/_/g, " ")}</span>}
                          <span className="text-muted" style={{ fontSize: "0.7rem" }}>{isExpanded ? "▲" : "▼"}</span>
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{ padding: "8px 14px", marginTop: -6, marginBottom: 8, background: "#fafbfe", border: "1px solid #e3e6ec", borderRadius: 10, fontSize: "0.78rem", lineHeight: 1.7 }}>
                          {pc && (
                            <div data-testid="comment-prospect-block" onClick={(e) => e.stopPropagation()}
                              style={{ padding: 8, borderRadius: 8, background: pc.bg, marginBottom: 6 }}>
                              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <span style={{ fontWeight: 600, color: "#667085" }}>Prospect:</span>
                                <b style={{ color: pc.color }}>{pc.label}</b>
                                <span className="text-muted">· {PROSPECT_SOURCE[prospect.stateSource] || prospect.stateSource}{prospect.handle ? ` · @${prospect.handle}` : ""}</span>
                                <select className="form-control form-control-sm ml-auto" style={{ width: 180 }} aria-label="Change prospect state"
                                  value="" onChange={(e) => changeProspectState(prospect, e.target.value)}>
                                  <option value="">Change state…</option>
                                  {PROSPECT_STATES.filter((x) => x.key !== prospect.state || prospect.stateSource !== "manual").map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
                                  {prospect.stateSource === "manual" && <option value="auto">Back to automatic</option>}
                                </select>
                              </div>
                              {prospect.reason && <div style={{ color: "#475467" }}>{prospect.reason}</div>}
                              {prospect.booking && prospect.booking.startsAt && <div style={{ color: "#6a3fb5" }}>Call booked: {fmt(prospect.booking.startsAt)}</div>}
                              <div className="text-muted" style={{ fontSize: "0.72rem" }}>{pc.hint}</div>
                            </div>
                          )}
                          {msgText && <div><span style={{ fontWeight: 600, color: "#667085" }}>Message text:</span><div style={{ whiteSpace: "pre-wrap", marginTop: 2 }}>{msgText}</div></div>}
                          {resultAction && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600, color: "#667085" }}>Result:</span> <span style={{ color: resultAction.includes("already") ? "#9a6700" : "#1a7f37" }}>{resultAction.replace(/_/g, " ")}</span></div>}
                          {resultText && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600, color: "#667085" }}>Detail:</span> <span className="text-muted">{resultText}</span></div>}
                          {a.error && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600, color: "#c0392b" }}>Error:</span> <span style={{ color: "#c0392b" }}>{a.error}</span></div>}
                          {a.note && <div style={{ marginTop: 4 }}><span style={{ fontWeight: 600, color: "#667085" }}>Note:</span> <span>{a.note}</span></div>}
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
            </>
          )}


      {/* ---- Stats drill-down modal ---- */}
      {statsDrill && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setStatsDrill(null); }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 16, width: 640, maxWidth: "92vw", maxHeight: "92vh", overflow: "auto", outline: "none", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 style={{ fontWeight: 700, margin: 0 }}>
                {statsDrill.title}
              </h5>
              <button className="btn btn-sm btn-outline-secondary m-0" onClick={() => setStatsDrill(null)}>&#10005;</button>
            </div>
            {statsDrill.loading ? (
              <div className="text-muted" style={{ textAlign: "center", padding: 30 }}>Loading&#8230;</div>
            ) : !statsDrill.activities.length ? (
              <div className="text-muted" style={{ textAlign: "center", padding: 30 }}>No matching ops found.</div>
            ) : (
              <div style={{ maxHeight: "60vh", overflow: "auto" }}>
                <table className="table table-sm" style={{ fontSize: "0.78rem" }}>
                  <thead><tr><th>Date</th><th>Platform</th><th>Type</th><th>Contact</th><th>Status</th></tr></thead>
                  <tbody>
                    {statsDrill.activities.map((a) => (
                      <tr key={a._id} style={{ cursor: "pointer" }}
                        onClick={() => {
                          const conv = conversations.find((c) => c.lastActivityId === a._id);
                          if (conv) setConvSidebar({ conversation: conv, thread: conv.thread || [], loading: !conv.thread?.length });
                        }}>
                        <td style={{ whiteSpace: "nowrap" }}>{fmt(a.createdAt)}</td>
                        <td>{PLATFORM_LABEL[a.platform] || a.platform}</td>
                        <td><span style={{ fontSize: "0.68rem", padding: "1px 6px", borderRadius: 6, background: "#f0f1f4", color: "#667085" }}>{OP_LABEL[a.op] || a.op}</span></td>
                        <td>{a.target && a.target.name ? truncate(a.target.name, 30) : (a.target && truncate(a.target.url, 30)) || "—"}</td>
                        <td><StatusBadge status={a.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Quick invite/message modal (per contact) ---- */}
      {quickModal && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget && !quickModal.busy) closeQuick(); }}
        >
          <div style={{ background: "#fff", borderRadius: 10, padding: 16, width: 480, maxWidth: "92vw", maxHeight: "92vh", overflow: "auto", outline: "none", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 style={{ fontWeight: 700, margin: 0 }}>
                {quickModal.op === "invite" ? "Send invite" : "Send message"} on {PLATFORM_LABEL[quickModal.contact.platform] || quickModal.contact.platform}
              </h5>
              <button className="btn btn-sm btn-outline-secondary m-0" onClick={closeQuick} disabled={quickModal.busy}>✕</button>
            </div>
            <div className="text-muted mb-3" style={{ fontSize: "0.82rem", lineHeight: 1.5 }}>
              <div><b>Contact:</b> {quickModal.contact.name || "—"}</div>
              {quickModal.contact.headline && <div><b>Headline:</b> {quickModal.contact.headline}</div>}
              {quickModal.contact.employer && <div><b>Company:</b> {quickModal.contact.employer}</div>}
              <div><b>Profile:</b> <a href={quickModal.contact.profileUrl} target="_blank" rel="noreferrer" style={{ wordBreak: "break-all" }}>{quickModal.contact.profileUrl}</a></div>
              {quickModal.contact.tier && (
                <div><b>Relevance:</b> <TierBadge tier={quickModal.contact.tier} score={quickModal.contact.score} /></div>
              )}
            </div>
            {quickModal.op === "invite" ? (
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="mb-0" style={{ fontSize: "0.82rem", fontWeight: 600 }}>Invite note (optional — 280&nbsp;chars max for LinkedIn)</label>
                  <button type="button" className="btn btn-sm btn-outline-primary m-0" style={{ fontSize: "0.72rem", padding: "1px 8px" }} disabled={quickModal.drafting || quickModal.busy} onClick={doDraft} title="Draft from brand settings + this contact's profile + conversation history">
                    {quickModal.drafting ? "Generating…" : "✨ Generate"}
                  </button>
                </div>
                <textarea
                  className="form-control form-control-sm"
                  rows={3}
                  maxLength={280}
                  placeholder="Personal connection note. Leave blank to send without a note."
                  value={quickModal.note}
                  onChange={(e) => { const _v = e.target.value; setQuickModal((m) => (m ? { ...m, note: _v } : m)); }}
                />
              </div>
            ) : (
              <div className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="mb-0" style={{ fontSize: "0.82rem", fontWeight: 600 }}>Message text *</label>
                  <button type="button" className="btn btn-sm btn-outline-primary m-0" style={{ fontSize: "0.72rem", padding: "1px 8px" }} disabled={quickModal.drafting || quickModal.busy} onClick={doDraft} title="Draft from brand settings + this contact's profile + conversation history">
                    {quickModal.drafting ? "Generating…" : "✨ Generate"}
                  </button>
                </div>
                <textarea
                  className="form-control form-control-sm"
                  rows={4}
                  placeholder="Your message…"
                  value={quickModal.text}
                  onChange={(e) => { const _v = e.target.value; setQuickModal((m) => (m ? { ...m, text: _v } : m)); }}
                />
                {!quickModal.text.trim() && <div className="text-danger small mt-1">Message text is required.</div>}
              </div>
            )}
            <div className="text-muted mb-3" style={{ fontSize: "0.74rem" }}>
              <i className="fas fa-info-circle mr-1" />
              This queues an operation for the <strong>Prigmar Connector</strong> browser extension.
              Make sure the extension is installed and online (green dot in the Accounts tab).
            </div>
            <div className="d-flex justify-content-end" style={{ gap: 8 }}>
              <button className="btn btn-sm btn-outline-secondary m-0" onClick={closeQuick} disabled={quickModal.busy}>Cancel</button>
              <button
                className="btn btn-sm btn-primary m-0"
                style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }}
                disabled={quickModal.busy || (quickModal.op === "message" && !quickModal.text.trim())}
                onClick={submitQuick}
              >
                {quickModal.busy ? "Queuing…" : quickModal.op === "invite" ? "Send invite" : "Send message"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Conversation sidebar (open from Invites/Messages) ---- */}
      {convSidebar && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1050, display: "flex", justifyContent: "flex-end" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConvSidebar(null); }}
        >
          <div style={{ width: 480, maxWidth: "92vw", background: "#fff", height: "100vh", overflow: "auto", boxShadow: "-4px 0 24px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #e3e6ec", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  {convSidebar.conversation?.contact?.name || "Conversation"}
                </div>
                <div className="text-muted" style={{ fontSize: "0.76rem" }}>
                  {PLATFORM_LABEL[convSidebar.conversation?.platform] || convSidebar.conversation?.platform}
                  {convSidebar.conversation?.brandName ? ` · ${convSidebar.conversation.brandName}` : ""}
                  {convSidebar.conversation?.conversionGoal !== "other" ? ` · Goal: ${(convSidebar.conversation?.conversionGoal || "").replace(/_/g, " ")}` : ""}
                </div>
              </div>
              <button className="btn btn-sm btn-outline-secondary m-0" onClick={() => setConvSidebar(null)}>✕</button>
            </div>

            {/* DM funnel — per-conversation goal override + stage/KB (Phase 3) */}
            <div style={{ padding: "10px 20px", borderBottom: "1px solid #eef0f4", background: "#fafbfc", fontSize: "0.78rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600 }}>Goal:</span>
                <select
                  className="form-control form-control-sm"
                  style={{ width: "auto", fontSize: "0.78rem", padding: "2px 6px" }}
                  value={convSidebar.conversation?.conversionGoal || "other"}
                  onChange={async (e) => {
                    const goal = e.target.value;
                    const id = convSidebar.conversation?._id;
                    if (!id) return;
                    try {
                      await updateConversation(id, { conversionGoal: goal });
                      setConvSidebar((s) => (s ? { ...s, conversation: { ...s.conversation, conversionGoal: goal, conversionGoalSource: "override" } } : s));
                      loadConversations();
                      addNotification("Goal updated for this conversation", "success");
                    } catch (_) { addNotification("Failed to update goal", "danger"); }
                  }}
                >
                  {[["buy_product", "Sell them a product"], ["become_customer", "Turn into a customer"], ["newsletter_signup", "Subscribe to newsletter"], ["join_course", "Join a course"], ["book_consulting", "Book a consultation"], ["join_community", "Join the community"], ["other", "Other / custom"]].map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
                {convSidebar.conversation?.conversionGoalSource === "override" && (
                  <span className="badge badge-info" style={{ fontSize: "0.62rem" }}>override</span>
                )}
              </div>
              {convSidebar.conversation?.funnel?.stage && (
                <div className="text-muted" style={{ marginTop: 6 }}>
                  Funnel stage: <b>{convSidebar.conversation.funnel.stage}</b>
                  {convSidebar.conversation.funnel.sentiment ? ` · ${convSidebar.conversation.funnel.sentiment}` : ""}
                </div>
              )}
              {convSidebar.conversation?.funnel?.kbSummary && (
                <div className="text-muted" style={{ marginTop: 4, fontStyle: "italic" }}>
                  What we know: {convSidebar.conversation.funnel.kbSummary}
                </div>
              )}
            </div>

            {/* Thread */}
            <div style={{ flex: 1, overflow: "auto", padding: "16px 20px" }}>
              {convSidebar.loading ? (
                <div className="text-muted" style={{ textAlign: "center", padding: 40 }}>Loading…</div>
              ) : !convSidebar.thread?.length ? (
                <div className="text-muted" style={{ textAlign: "center", padding: 40 }}>No messages yet.</div>
              ) : (
                convSidebar.thread.map((m, i) => (
                  <div key={m._id || i} style={{ marginBottom: 12, display: "flex", flexDirection: m.direction === "out" ? "row-reverse" : "row", gap: 8 }}>
                    <div style={{
                      maxWidth: "75%", padding: "8px 12px", borderRadius: 10,
                      background: m.direction === "out" ? "#0b3d91" : "#f0f1f4",
                      color: m.direction === "out" ? "#fff" : "#1f2937",
                      fontSize: "0.82rem", lineHeight: 1.5,
                    }}>
                      <div>{m.text}</div>
                      <div style={{ fontSize: "0.64rem", marginTop: 4, opacity: 0.6 }}>{fmt(m.createdAt)}</div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* AI Reply button */}
            {convSidebar.conversation?.status === "active" && (
              <div style={{ padding: "12px 20px", borderTop: "1px solid #e3e6ec", display: "flex", gap: 8 }}>
                <button className="btn btn-sm btn-primary m-0" style={{ backgroundColor: "#0b3d91", borderColor: "#0b3d91" }}
                  onClick={async () => {
                    try {
                      await generateAiReply(convSidebar.conversation._id);
                      addNotification("AI reply requested.", "success");
                    } catch (e) { addNotification((e && e.message) || "Could not request AI reply.", "danger"); }
                  }}>
                  <i className="fas fa-robot mr-1" />Generate AI reply
                </button>
                <button className="btn btn-sm btn-outline-secondary m-0"
                  onClick={() => setConvSidebar((s) => ({ ...s, conversation: { ...s.conversation, status: "converted", convertedAt: new Date() } }))}>
                  Mark converted
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---- Contact timeline modal ---- */}
      {timelineContact && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={(e) => { if (e.target === e.currentTarget) setTimelineContact(null); }}>
          <div style={{ background: "#fff", borderRadius: 10, padding: 16, width: 640, maxWidth: "92vw", maxHeight: "92vh", overflow: "auto", outline: "none", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5 style={{ fontWeight: 700, margin: 0 }}>
                Timeline: {timelineContact.name || timelineContact.profileUrl}
              </h5>
              <button className="btn btn-sm btn-outline-secondary m-0" onClick={() => setTimelineContact(null)}>&#10005;</button>
            </div>
            {timelineContact.name && <div className="text-muted mb-2" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>{timelineContact.profileUrl}</div>}
            {timelineContact.loading ? (
              <div className="text-muted" style={{ textAlign: "center", padding: 20 }}>Loading&#8230;</div>
            ) : !timelineContact.activities?.length ? (
              <div className="text-muted" style={{ textAlign: "center", padding: 20 }}>No activity found for this contact.</div>
            ) : (
              <div style={{ maxHeight: "60vh", overflow: "auto" }}>
                <table className="table table-sm" style={{ fontSize: "0.78rem" }}>
                  <thead><tr><th>Date</th><th>Platform</th><th>Type</th><th>Status</th><th>Result</th></tr></thead>
                  <tbody>
                    {timelineContact.activities.map((a) => (
                      <tr key={a._id}>
                        <td style={{ whiteSpace: "nowrap" }}>{fmt(a.createdAt)}</td>
                        <td>{PLATFORM_LABEL[a.platform] || a.platform}</td>
                        <td><span style={{ fontSize: "0.68rem", padding: "1px 6px", borderRadius: 6, background: "#f0f1f4", color: "#667085" }}>{OP_LABEL[a.op] || a.op}</span></td>
                        <td><StatusBadge status={a.status} /></td>
                        <td className="text-muted" style={{ maxWidth: 160 }}>
                          {a.result && (a.result.action || a.result.status || (a.result.text && a.result.text.slice(0, 60)))
                            ? (a.result.action || a.result.status || truncate(a.result.text, 60)).replace(/_/g, " ")
                            : a.error ? <span className="text-danger">{truncate(a.error, 60)}</span>
                            : a.payload && a.payload.note ? truncate(a.payload.note, 60)
                            : a.payload && a.payload.text ? truncate(a.payload.text, 60)
                            : <span className="text-muted">&mdash;</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityConnectors;
